/**
 * Translations.
 *
 * A typed dictionary rather than an i18n library: there is one screen to
 * translate, and `t()` here has the same shape as the one a library would give,
 * so swapping in i18next later is a change of implementation, not of call sites.
 *
 * Every locale must supply every key — `Dictionary` is derived from English, so
 * a missing string is a type error rather than a blank space in production.
 */

export const LOCALES = [
  { code: "en", label: "English", short: "EN" },
  { code: "fil", label: "Filipino", short: "FIL" },
] as const;

export type Locale = (typeof LOCALES)[number]["code"];

const en = {
  "login.welcome": "Welcome back to MRM",
  "login.subtitle": "Enter your employee ID and password to sign in.",
  "login.employeeId": "Employee ID",
  "login.employeeIdPlaceholder": "eg. 12345678",
  "login.password": "Password",
  "login.passwordPlaceholder": "Enter your password",
  "login.showPassword": "Show password",
  "login.hidePassword": "Hide password",
  "login.resetPassword": "Reset Password?",
  "login.continue": "Continue",
  "login.signingIn": "Signing in…",
  "login.errorGeneric": "Could not sign you in.",
  "login.errorEmployeeIdRequired": "Enter your employee ID",
  "login.errorPasswordRequired": "Enter your password",
  "login.mustChangePassword": "Choose a new password before you continue.",
  "login.lockedTitle": "Too many sign-in attempts",
  "login.lockedBody":
    "For your security, sign-in is paused on this device. Please wait {time} and try again. Nothing is wrong with your account.",
  "login.lockedAction": "Try again in {time}",
  "login.hintBoth": "Enter your employee ID and password to continue.",
  "login.hintId": "Enter your employee ID — at least {n} characters.",
  "login.hintPassword": "Enter your password — at least {n} characters.",

  "reset.title": "Welcome back to MRM",
  "reset.subtitle": "Enter the Employee ID and the reason for the password reset.",
  "reset.employeeIdPlaceholder": "Enter employee id",
  "reset.reason": "Reason for Request",
  "reset.reasonPlaceholder": "Select reason",
  "reset.errorReasonRequired": "Choose a reason",
  "reset.submit": "Reset Password",
  "reset.submitting": "Sending…",
  "reset.back": "Back to Sign In",
  "pw.backHome": "Back to home",
  "reset.sentTitle": "Password Reset",
  "reset.sentBody":
    "Your request has been submitted successfully. You will be notified by your department head when the new temporary password is available for login.",
  "reset.sentDismiss": "Dismiss",
  "reset.errorGeneric": "Could not send your request.",
  "reset.expiredSubtitle":
    "Your password has expired. Please submit a reset request to continue.",
  "reset.lockedSubtitle":
    "This account is locked after too many incorrect attempts. Send a reset request to unlock it.",

  "showcase.title": "Membership Management",
  "showcase.subtitle":
    "Manage member information, access levels, and membership activities in one place.",
  "showcase.pwTitle": "Time to choose a new one",
  "showcase.pwFirstTitle": "One last step",
  "showcase.pwFirstBody":
    "The password you signed in with was issued to you. Choose one only you know.",
  "showcase.pwIdle": "Keep your account yours",
  "showcase.pwBody": "Setting a new password signs out every other session, everywhere.",

  "nav.dashboard": "Dashboard",
  "nav.members": "Members",
  "nav.memberAnalytics": "Member Analytics",
  "nav.nonMembers": "Non Member",
  "nav.employees": "Employee",
  "nav.locationConfig": "Location Master Config",
  "nav.provinces": "Provinces",
  "nav.cities": "City",
  "nav.barangays": "Barangay",
  "nav.maps": "Maps",
  "nav.memberPopulation": "Member & Population",
  "map.points": "map points",
  "map.subtitle": "Where members are, and where the people are, on one map.",
  "map.held": "The map is held still until they arrive — moving it now would only ask again.",
  "map.stop": "Stop loading",
  "map.stopped": "Stopped. Move the map to try again.",
  "map.cacheDate": "Cache date: {time}",
  "map.expiresAt": "expires at {time}",
  "map.cached": "cached — refresh for the latest",
  "map.refreshFailed": "Could not refresh the map.",
  "map.populationByClub": "Population by Club",
  "map.philippinesPopulation": "Philippines Population",
  "map.members": "S&R Member",
  "map.view": "Map view",
  "map.allClubs": "All clubs",
  "map.clubsSelected": "{count} clubs selected",
  "map.all": "All",
  "map.population": "population",
  "map.noPopulation": "Population not recorded",
  "map.membersUnit": "members",
  "map.level.province": "Provinces — zoom in for cities",
  "map.level.city": "Cities — zoom in for barangays",
  "map.level.barangay": "Barangays in view",
  "map.inView": "in view",
  "map.level.memberClubs": "Members per club — zoom in for where they live",
  "map.level.memberBarangays": "Members by barangay, for the selected clubs",
  "map.unit.province": "provinces",
  "map.unit.city": "cities",
  "map.unit.barangay": "barangays",
  "map.unit.club": "clubs",
  "map.level.clubs": "Clubs, with their city's population — zoom in for the barangays",
  "nav.yellowPage": "Yellow Page",
  "nav.configuration": "Configuration",
  "nav.group.overview": "Overview",
  "nav.group.membership": "Membership",
  "nav.group.directory": "Directory",
  "nav.group.geography": "Geography",
  "nav.group.system": "System",
  "page.activityLog": "Activity Log",
  "page.memberActivityLog": "Member Activity Log",
  "page.memberDetail": "Detail View",
  "page.yellowPageCategories": "Yellow Page Categories",
  "page.profile": "My Profile",
  "page.changePassword": "Change Password",
  "shell.home": "Home",
  "shell.system": "MRM system",
  "shell.search": "Search...",
  "shell.collapse": "Collapse sidebar",
  "shell.openMenu": "Open menu",
  "shell.closeMenu": "Close menu",
  "shell.expand": "Expand sidebar",
  "shell.roleSuperuser": "Superuser",
  "shell.roleAdmin": "Admin",
  "shell.roleMember": "Member",
  "shell.editProfile": "Edit Profile",
  "shell.profilePicture": "Profile Picture",
  "shell.pictureUpdated": "Profile picture updated.",
  "shell.pictureFailed": "Could not update your picture.",
  "shell.logout": "Logout",
  "shell.viewProfile": "View profile",
  "shell.changePassword": "Change password",
  "shell.language": "Language",
  "shell.theme": "Theme",
  "shell.accountMenu": "Account menu",
  "shell.navSearch": "Filter menu",
  "shell.noNavMatch": "No menu item matches",
  "shell.mainNav": "Main navigation",
  "shell.skipToContent": "Skip to content",
  "profile.subtitle": "Your account, as HR and the system have it.",
  "profile.editProfile": "Edit profile",
  "profile.changePicture": "Change picture",
  "profile.removePicture": "Remove picture",
  "profile.cancel": "Cancel",
  "profile.save": "Save changes",
  "profile.saving": "Saving…",
  "profile.fullName": "Full name",
  "profile.email": "Email address",
  "profile.emailHint": "Where temporary passwords and notices are sent.",
  "profile.employeeId": "Employee ID",
  "profile.employeeIdHint": "Assigned by HR; not editable here.",
  "profile.club": "Home club",
  "profile.noClub": "No club assigned",
  "profile.roles": "Roles",
  "profile.status": "Account status",
  "profile.lastLogin": "Last sign-in",
  "profile.passwordChanged": "Password last changed",
  "profile.created": "Account created",
  "profile.never": "Never",
  "profile.errorName": "Enter your name.",
  "profile.errorEmail": "Enter a valid email address.",
  "profile.updated": "Profile updated.",
  "profile.saveFailed": "Could not save your profile.",
  "profile.pictureRemoved": "Picture removed.",
  "profile.sectionAccount": "Account",
  "profile.sectionPosting": "Temporary posting",
  "profile.sectionSecurity": "Security",
  "profile.postingNone": "No temporary posting on record.",
  "profile.postingActive": "Active today",
  "profile.postingScheduled": "Not in effect today",
  "profile.postingClub": "Posted to",
  "profile.postingFrom": "From",
  "profile.postingUntil": "Until",
  "profile.postingReason": "Reason",
  "profile.superuser": "Superuser — every permission",
  "profile.forceChange": "Password change required",
  "profile.forceChangeYes": "Yes, before anything else",
  "profile.forceChangeNo": "No",
  "profile.openRequest": "Open password request",
  "profile.openRequestNone": "None",
  "profile.updated_at": "Last updated",
  "profile.permissions": "Permissions",
  "profile.permissionsCount": "{count} granted",
  "status.active": "Active",
  "status.password_expired": "Password expired",
  "status.account_locked": "Locked",
  "status.inactive": "Inactive",
  "status.deleted": "Deleted",

  "dash.kicker": "Everything you manage, in one place.",
  "dash.title": "Dashboard",
  "dash.statMembers": "Members",
  "dash.statArchived": "Archived members",
  "dash.statArchivedHint": "Deleted, and kept for the record",
  "dash.statNonMembers": "Non-members",
  "dash.statRecent": "{n} in the last 30 days",
  "dash.statPromoCodes": "Active promo codes",
  "dash.statExpired": "{n} expired",
  "dash.panelMembers": "Membership",
  "dash.byStatus": "By status",
  "dash.byTier": "By tier",
  "dash.byAccountType": "By account type",
  "dash.nothingYet": "Nothing to count yet.",
  "dash.location": "Location & Logs",
  "dash.locationText": "Master config and audit trails",
  "dash.workspace": "Workspace",
  "dash.workspaceText": "Explore your modules",
  "dash.addMember": "Add Member",
  "dash.addMemberText": "Enroll a new member",
  "dash.members": "Members",
  "dash.membersText": "Browse the member directory",
  "dash.employees": "Employees",
  "dash.employeesText": "Manage staff records",
  "dash.nonMember": "Non Member",
  "dash.nonMemberText": "Register a prospect",
  "dash.province": "Province",
  "dash.provinceText": "Manage provinces",
  "dash.city": "City / Location",
  "dash.cityText": "Manage cities & areas",
  "dash.barangay": "Barangay",
  "dash.barangayText": "Manage barangays",
  "dash.employeeLog": "Employee Activity Log",
  "dash.employeeLogText": "Audit trail for staff",
  "dash.memberLog": "Member Activity Log",
  "dash.memberLogText": "Audit trail for members",
  "dash.yellowPage": "Yellow Page",
  "dash.yellowPageText": "Business directory",
  "dash.maps": "Maps",
  "dash.mapsText": "Coverage & population",
  "dash.configuration": "Configuration",
  "dash.configurationText": "System settings",
  "dash.restricted": "Restricted for your role",
  "dash.restrictedHint": "Your role does not include access to this screen.",

  "pw.title": "Change password",
  "pw.forced": "Replace the temporary password you were given with one only you know.",
  "pw.noExpiry": "Your password does not expire, but you can change it any time.",
  "idle.warnTitle": "Are you still there?",
  "idle.warnBody":
    "There has been no activity for a while, so you will be signed out in {n} seconds.",
  "idle.stay": "I'm still here",
  "idle.signedOut":
    "You were signed out after {n} minutes without activity. Sign in to carry on.",
  "pw.expired": "Your password has expired. Change it to keep signing in.",
  "pw.today": "Your password expires today.",
  "pw.inDays": "Your password expires in {days} days.",
  "pw.inOneDay": "Your password expires tomorrow.",
  "pw.current": "Current password",
  "pw.new": "New password",
  "pw.confirm": "Confirm new password",
  "pw.errorCurrent": "Enter your current password",
  "pw.ruleLength": "8 to 20 characters",
  "pw.ruleLower": "A lowercase letter",
  "pw.ruleUpper": "An uppercase letter",
  "pw.ruleDigit": "A number",
  "pw.ruleSymbol": "A symbol",
  "pw.ruleDifferent": "Different from your current password",
  "pw.ruleMatch": "Both new password fields match",
  "pw.submit": "Set New Password",
  "pw.subtitle": "Enter a new password for your account",
  "pw.requirements": "Password Requirements",
  "pw.saving": "Saving…",
  "pw.success": "Password updated.",
  "pw.failed": "Could not change your password.",
  "pw.tokenExpired": "Your sign-in has timed out. Sign in again to set your password.",
  "pw.expiresIn": "This page expires in {time}. After that you will have to sign in again.",

  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.cancel": "Cancel",
  "common.create": "Create",
  "common.submit": "Submit",
  "common.saving": "Saving…",
  "common.loading": "Loading…",
  "common.loadMore": "Load more",
  "common.close": "Close",
  "common.deleted": "Deleted.",
  "common.created": "Created.",
  "common.updated": "Updated.",
  "common.saved": "Saved.",
  "common.couldNotDelete": "Could not delete.",
  "common.staleRecord":
    "This record was changed by someone else. It has been reloaded; please make your change again.",
  "common.recordBusy": "Someone else is saving this record. Wait a moment and try again.",
  "common.couldNotSave": "Could not save.",
  "common.nothingYet": "Nothing yet.",
  "common.shownOfTotal": "{shown} of {total}",
  "common.confirmDelete": "Delete {name}? This cannot be undone.",
  "common.confirmDeleteTitle": "Delete {model}",
  "common.charactersRemaining": "{count} characters remaining",
  "common.comingSoon": "Coming soon",
  "common.onItsWay": "{section} is on its way.",
  "common.of": "of",
  "common.showing": "Showing",
  "common.rowsPerPage": "Rows per page",
  "common.firstPage": "First page",
  "common.previousPage": "Previous page",
  "common.nextPage": "Next page",
  "common.lastPage": "Last page",
  "cfg.subtitle": "The lists, limits and roles the rest of the system runs on.",
  "cfg.title": "Configurations",
  "cfg.notes": "Notes",
  "cfg.reasons": "Reasons",
  "cfg.accessPolicy": "Access policy",
  "cfg.passwordExpiry": "Password Expiry",
  "cfg.promoCodes": "Promo Codes",
  "cfg.businessType": "Business Type",
  "cfg.businessUnit": "Business Unit",
  "cfg.roles": "Roles",
  "cfg.preOpening": "Pre-opening",
  "cfg.complimentary": "Complimentary",
  "cfg.endorsement": "Endorsement",
  "cfg.notesText": "Configure notes for pre-opening, complementary and endorsement",
  "cfg.requestedBy": "Requested By",
  "cfg.idleTitle": "Idle Time",
  "cfg.minutes": "Minutes",
  "cfg.days": "Days",
  "cfg.businessTypeText":
    "Configure the list of business types for member registration and classification",
  "cfg.businessUnitText": "Configure the list of clubs for business unit management",
  "cfg.businessTypeField": "Business type",
  "cfg.currentBusinessTypes": "Current business types",
  "cfg.addBusinessType": "Add new business type",
  "cfg.businessTypeDeleted": "Business type deleted.",
  "cfg.businessTypeDeleteFailed": "Could not delete the business type.",
  "cfg.noBusinessTypes": "No business types yet.",
  "cfg.club": "Club",
  "cfg.clubName": "Club name",
  "cfg.clubCode": "Club code",
  "cfg.city": "City",
  "cfg.address": "Address",
  "cfg.latitude": "Latitude",
  "cfg.longitude": "Longitude",
  "cfg.addClub": "Add new club",
  "cfg.clubDeleted": "Club deleted.",
  "cfg.clubDeleteFailed": "Could not delete the club.",
  "cfg.noClubs": "No clubs yet.",
  "roles.intro":
    "A role is a set of things a person is allowed to do. Give someone a role and they can do everything in it.",
  "roles.role": "Role",
  "roles.addRole": "Add new role",
  "roles.builtIn": "Built-in",
  "roles.builtInName": "A built-in role keeps its name; its permissions can still change.",
  "roles.builtInNoDelete": "Built-in roles cannot be deleted",
  "roles.confirmDeleteTitle": "Delete role",
  "roles.confirmDelete": 'Delete the role "{name}"? People in it lose its permissions.',
  "roles.none": "No roles yet.",
  "roles.deleted": "Role deleted.",
  "roles.deleteFailed": "Could not delete the role.",
  "roles.created": "Role created.",
  "roles.updated": "Role updated.",
  "roles.saveFailed": "Could not save the role.",
  "roles.back": "Back to roles",
  "roles.new": "New role",
  "roles.edit": "Edit role",
  "roles.tickIntro":
    "Tick what people in this role may do. Each row is a kind of record; each column is a verb.",
  "roles.name": "Role name",
  "roles.namePlaceholder": "e.g. Club Manager",
  "roles.description": "Description",
  "roles.descriptionPlaceholder": "e.g. Runs one club day to day",
  "roles.groupMembers": "Members",
  "roles.groupPeople": "People & access",
  "roles.groupGeography": "Geography",
  "roles.groupCatalogue": "Catalogue & settings",
  "roles.groupCount": "{selected} of {total}",
  "roles.details": "Details",
  "roles.permissions": "Permissions",
  "roles.quickStart": "Quick start:",
  "roles.seeEverything": "Can see everything",
  "roles.doEverything": "Can do everything",
  "roles.clearAll": "Clear all",
  "roles.count": "{selected} of {total} permissions",
  "roles.kindOfRecord": "Kind of record",
  "roles.noPermissions": "No permissions defined.",
  "roles.createRole": "Create role",
  "roles.saveRole": "Save role",
  "roles.forEvery": "{verb} for every kind of record",
  "roles.view": "View",
  "roles.viewHint": "can open and read",
  "roles.add": "Add",
  "roles.addHint": "can create new",
  "roles.change": "Edit",
  "roles.changeHint": "can change existing",
  "roles.remove": "Delete",
  "roles.removeHint": "can remove",
  "roles.labelGroup": "Role",
  "roles.labelUser": "User (employee)",
  "policy.text": "How long a session may sit idle, and how often a password must change",
  "policy.globalText": "Every role and user has these unless changed below",
  "policy.roleText": "Every {role} has these unless changed for the person",
  "policy.global": "Global {title}",
  "policy.roleBased": "Role Based {title}",
  "policy.individual": "Individual {title}",
  "policy.system": "System",
  "policy.addEmployee": "Add Employee",
  "policy.employee": "Employee",
  "policy.chooseEmployee": "Choose an employee",
  "policy.nobody": "Nobody has their own value; everyone inherits.",
  "policy.leaveTitle": "Leave without saving?",
  "policy.leaveUnsaved": "You have unsaved changes. Leave without saving them?",
  "policy.saveChanges": "Save changes",
  "policy.discard": "Discard",
  "policy.unsavedCount": "{n} unsaved",
  "policy.default": "Default",
  "policy.orgName": "Organisation name",
  "policy.orgHint": "No organisation exists yet; this creates it.",
  "policy.orgCreated": "Organisation created.",
  "policy.orgCreateFailed": "Could not create the organisation.",
  "reasons.intro":
    "Configure the options for selecting reason for multiple scenarios across the application",
  "reasons.cardReplacement": "Card Replacement",
  "reasons.blacklist": "Blacklist",
  "reasons.cancellation": "Cancellation",
  "reasons.cardReplacementHeading": "Card Replacement Reasons",
  "reasons.blacklistHeading": "Blacklist Reasons",
  "reasons.cancellationHeading": "Cancellation Reasons",
  "reasons.amendmentReason": "Amendment Reason",
  "reasons.amendmentType": "Amendment Type",
  "reasons.reason": "Reason",
  "reasons.addReason": "Add new reason",
  "reasons.addNew": "Add new",
  "reasons.endorsementHeading": "Endorsement Reason",
  "reasons.notesReason": "Notes Reason",
  "reasons.notesTitle": "Notes Title",
  "reasons.endorsement": "Endorsement",
  "reasons.endorsedBy": "Endorsed By",
  "reasons.endorsedByTitle": "Notes Complimentary Endorsed By",
  "reasons.endorsedByList": "Endorsed By List",
  "reasons.name": "Name",
  "reasons.approvedBy": "Approved By",
  "reasons.approvedByTitle": "Notes Complimentary Approve By",
  "reasons.approvedByList": "Approve By List",
  "reasons.editTitle": "Edit {title}",
  "reasons.createTitle": "Create {title}",
  "reasons.typeSomething": "Type Something",
  "reasons.confirmDelete": 'Delete "{name}"?',
  "promo.showExpired": "Show expired",
  "promo.noDescription": "No description",
  "promo.title": "Promo Codes",
  "promo.intro": "Manage promotional codes for member discounts and offers.",
  "promo.add": "Add new promo code",
  "promo.validUntil": "Valid Until",
  "promo.deleted": "Promo code deleted.",
  "promo.deleteFailed": "Could not delete the promo code.",
  "promo.none": "No promo codes yet.",
  "promo.confirmDelete": "Delete promo code {code}?",
  "promo.edit": "Edit Promo Code",
  "promo.create": "Create Promo Code",
  "promo.code": "Promo Code",
  "promo.description": "Description",
  "promo.enter": "Enter",
  "promo.updated": "Promo code updated.",
  "promo.created": "Promo code created.",
  "promo.saveFailed": "Could not save the promo code.",

  "rp.search": "Search...",
  "load.loading": "Loading {subject}…",
  "load.searching": "Searching {subject}…",
  "load.filtering": "Applying filters…",
  "load.sorting": "Reordering {subject}…",
  "load.paging": "Fetching the next page…",
  "load.saving": "Saving…",
  "load.deleting": "Removing…",
  "load.exporting": "Preparing the export…",
  "load.stillWorking": "Still working — a large search can take a moment.",
  "rp.searchHistory": "Search history",
  "rp.suggestions": "Suggestions",
  "rp.removeSearch": "Remove {term}",
  "rp.timeRange": "Time Range",
  "rp.searchHistoryKept": "Searches you have not repeated in a month are removed.",
  "rp.lastHour": "Last hour",
  "rp.last24h": "Last 24 hours",
  "rp.last7d": "Last 7 days",
  "rp.last4w": "Last 4 weeks",
  "rp.allTime": "All Time",
  "rp.filters": "Filters",
  "rp.clearFilter": "Clear filter",
  "rp.clearFilters": "Clear {count} filters",
  "rp.clearFiltersBtn": "Clear filters",
  "rp.export": "Export",
  "rp.moreActions": "More actions",
  "rp.loadFailedTitle": "Could not load this list",
  "rp.loadFailedBody":
    "The request did not come back. Check your connection and try again — nothing has been changed.",
  "rp.retry": "Try again",
  "rp.confirmDeleteTitle": "Delete this {singular}?",
  "rp.refresh": "Refresh",
  "rp.columns": "Columns",
  "rp.updatedAt": "Updated At",
  "rp.updatedBy": "Updated By",
  "rp.customizeColumns": "Customize Columns",
  "rp.add": "Add {title}",
  "rp.back": "Back",
  "rp.matching": "matching “{search}”",
  "rp.filterOn": "{count} filter on",
  "rp.filtersOn": "{count} filters on",
  "rp.noMatch": "No {plural} match",
  "rp.tryAnother": "Try another spelling, or widen the filters.",
  "rp.noneYet": "No {plural} yet",
  "rp.entriesAppear": "Entries appear here as things happen.",
  "rp.firstAdd": "The first {singular} you add will show up here.",
  "rp.showing": "Showing",
  "rp.pageN": "Page {n}",
  "mem.csvRowsOnPage": "Rows {from}–{to} of {total}",
  "mem.csvLine": "Line",
  "mem.submitThisPage": "Submit this page ({count})",
  "mem.submitAll": "Submit all ({count})",
  "mem.csvMissingColumns":
    "This file is missing {columns}. Download the template and use its header.",
  "mem.csvExpectedHeader": "Expected header:",
  "mem.csvProgress": "{done} of {total} rows",
  "mem.csvTally": "{succeeded} went through, {failed} did not",
  "mem.csvStop": "Stop",
  "mem.csvSending": "Sending the rows. Leave this open until it is done.",
  "mem.csvRefused": "{count} rows were refused — they are below, with the reason beside each.",
  "mem.csvOutcome": "What happened to each row",
  "mem.csvPending": "Pending",
  "mem.csvSucceeded": "Success",
  "mem.csvNoneSucceeded": "No rows have gone through yet.",
  "mem.csvNonePending": "Nothing left — every row went through.",
  "mem.csvResult": "Why it was refused",
  "mem.csvStopped":
    "Stopped. What had been written is written — send the same file again to finish the rest.",
  "mem.csvUnchanged": "{count} already had what the file asked for.",
  "mem.csvFixFailed": "Correct the {count} rows that failed",
  "mem.csvFileHeader": "This file's header:",
  "mem.csvReady": "{rows} rows read from {name}.",
  "mem.reviewAndEdit": "Review and edit",
  "rp.rowActions": "Row actions",
  "rp.view": "View",
  "rp.stampCreated": "Created",
  "rp.stampUpdated": "Updated",
  "rp.id": "ID",
  "rp.customizeColumn": "Customize Column",
  "rp.showHide": "Show/Hide columns in the table.",
  "rp.ok": "OK",
  "rp.clear": "Clear",
  "rp.applyFilter": "Apply Filter",
  "rp.addSingular": "Add {singular}",
  "rp.editSingular": "Edit {singular}",
  "rp.save": "Save",
  "rp.deleted": "{singular} deleted.",
  "rp.deleteFailed": "Could not delete {singular}.",
  "rp.confirmDelete": "Delete this {singular}? This cannot be undone.",
  "rp.created": "{singular} created.",
  "rp.updated": "{singular} updated.",
  "rp.saveFailed": "Could not save {singular}.",
  "rp.chooseFirst": "Choose a {parent} first",
  "rp.select": "Select {label}",
  "rp.selectDots": "Select…",
  "rp.yes": "Yes",
  "rp.no": "No",
  "rp.changeImage": "Change image",
  "rp.dropImage": "Drag & Drop or click to upload (jpg, png)",
  "rp.fileAttached": "File attached ({kb} KB) — click to change",
  "rp.dropFile": "Drag & Drop or click to upload the file",
  "geo.provinces": "Provinces",
  "geo.province": "Province",
  "geo.cities": "Cities",
  "geo.city": "City",
  "geo.barangays": "Barangays",
  "geo.barangay": "Barangay",
  "geo.name": "Name",
  "geo.code": "Code",
  "geo.population": "Population",
  "geo.latitude": "Latitude",
  "geo.longitude": "Longitude",
  "geo.createdBy": "Created By",
  "geo.createdAt": "Created At",
  "geo.createdByFilter": "Created by",
  "geo.createdFrom": "Created from",
  "geo.createdTo": "Created to",

  "theme.light": "Light theme",
  "theme.dark": "Dark theme",
  "theme.lightValue": "Light",
  "theme.darkValue": "Dark",
  "language.label": "Language",

  "page.mapsText": "Coverage and population on a map.",
  "page.nothingYet": "There is nothing to do here yet.",
  "page.backToDashboard": "Back to the dashboard",

  "nm.singular": "Non Member",
  "nm.date": "Date",
  "nm.name": "Name",
  "nm.birthDate": "Birth Date",
  "nm.promoCode": "Promo Code",
  "nm.promoCodeFilter": "Promo code",
  "nm.club": "Club",
  "nm.actor": "Actor",
  "nm.visitedFrom": "Visited from",
  "nm.visitedTo": "Visited to",
  "nm.firstName": "First Name",
  "nm.middleName": "Middle Name",
  "nm.lastName": "Last Name",

  "log.entry": "Entry",
  "log.memberNumberSearch": "Member Number",
  "log.memberAccountNumber": "Member Account Number",
  "log.modification": "Type of Modification",
  "log.from": "From",
  "log.to": "To",
  "log.modifiedAt": "Modified At",
  "log.modifiedBy": "Modified By",
  "log.clubLocation": "Club Location",
  "log.fromDate": "From date",
  "log.toDate": "To date",
  "log.employeeSearch": "Employee ID / Name",
  "log.employee": "Employee",
  "log.reason": "Reason",
  "log.status": "Status",
  "log.actor": "Actor",
  "log.handledBy": "Handled by",
  "log.employeeNo": "Employee No.",
  "log.name": "Name",
  "log.date": "Date",

  "ypc.category": "Category",

  "yp.title": "Yellow Pages",
  "yp.description": "Every business and office listed across the Philippines.",
  "yp.categories": "Categories",
  "yp.addListing": "Add Listing",
  "yp.editListing": "Edit Listing",
  "yp.listing": "Listing",
  "yp.searchListings": "Search listings",
  "yp.listings": "listings",
  "yp.anyCity": "Any city",
  "yp.anyCategory": "Any category",
  "yp.myTags": "My tags",
  "yp.tagged": "Tagged as a favourite.",
  "yp.tagRemoved": "Tag removed.",
  "yp.tagFailed": "Could not update the tag.",
  "yp.tagFavourite": "Tag as favourite",
  "yp.removeTag": "Remove tag",
  "yp.listingDeleted": "Listing deleted.",
  "yp.deleteFailed": "Could not delete the listing.",
  "yp.deleteListing": "Delete listing",
  "yp.confirmDelete": "Delete {name}? This cannot be undone.",
  "yp.thisListing": "this listing",
  "yp.noListings": "No listings found",
  "yp.tryDifferent": "Try a different search, or clear the filters.",
  "yp.listingsAppear": "Listings appear here once businesses are added to the directory.",
  "yp.countOf": "{shown} of {total} listings",
  "yp.setOutFrom": "Set out from",
  "yp.homeClub": "Home club",
  "yp.temporaryClub": "Temporary club",
  "yp.directionsFrom": "Directions from {club}",
  "yp.location": "Location",
  "yp.hideMap": "Hide map",
  "yp.showMap": "Show map",
  "yp.findingWay": "Finding the way…",
  "yp.routeFailed": "The route could not be worked out just now; the map shows both ends.",
  "yp.byCar": "{distance} · about {time} by car",
  "yp.straightLine": "{km} km in a straight line",
  "yp.hideSteps": "Hide {n} steps",
  "yp.showSteps": "Show {n} steps",
  "yp.turnLeft": "Turn left",
  "yp.turnRight": "Turn right",
  "yp.bearLeft": "Bear left",
  "yp.bearRight": "Bear right",
  "yp.sharpLeft": "Sharp left",
  "yp.sharpRight": "Sharp right",
  "yp.straight": "Continue straight",
  "yp.uturn": "Make a U-turn",
  "yp.arrive": "Arrive at the destination",
  "yp.headOutOn": "Head out on {road}",
  "yp.headOut": "Head out",
  "yp.roundabout": "Take the roundabout",
  "yp.merge": "Merge",
  "yp.keep": "Keep {direction}",
  "yp.ahead": "ahead",
  "yp.continue": "Continue",
  "yp.onto": "onto {road}",
  "yp.category": "Yellow Page Category",
  "yp.selectCategory": "Select category",
  "yp.addCategory": "Add a category",
  "yp.locationHint": "The city or municipality.",
  "yp.selectCity": "Select city",
  "yp.fullAddress": "Full Address",
  "yp.phone": "Phone",
  "yp.photos": "Photos",
  "yp.dropPhotos": "Drag & Drop to upload image files (jpg, png)",
  "yp.photosOf": "{n} of {max}",
  "yp.removePhoto": "Remove photo",
  "yp.newCategory": "New category",
  "yp.newCategoryHint": "It becomes available to every listing straight away.",
  "yp.add": "Add",
  "yp.categoryAdded": "Category “{name}” added.",
  "yp.categoryFailed": "Could not add the category.",
  "yp.imageFailed": "Could not read that image.",
  "yp.listingUpdated": "Listing updated.",
  "yp.listingAdded": "Listing added.",
  "yp.saveFailed": "Could not save the listing.",

  "emp.employeeNumber": "Employee Number",
  "emp.search": "Any part of a name, or a full Employee ID or email",
  "emp.name": "Name",
  "emp.firstName": "First Name",
  "emp.middleName": "Middle Name",
  "emp.lastName": "Last Name",
  "emp.emailAddress": "Email Address",
  "emp.club": "Club",
  "emp.temporaryClub": "Temporary Club",
  "emp.temporaryClubFilter": "Temporary club",
  "emp.role": "Role",
  "emp.manager": "Manager",
  "emp.status": "Status",
  "emp.statusActive": "Active",
  "emp.statusLocked": "Account Locked",
  "emp.statusExpired": "Password Expired",
  "emp.statusInactive": "Inactive",
  "emp.statusDeleted": "Deleted",
  "emp.passwordRequest": "Password request",
  "emp.sectionPicture": "Picture",
  "emp.sectionIdentity": "Identity",
  "emp.sectionAccess": "Access",
  "emp.sectionClub": "Club",
  "emp.sectionPassword": "Password",
  "emp.isSuperuser": "Superuser",
  "emp.forcePasswordChange": "Force Password Change",
  "emp.isPasswordRequest": "Password Request",
  "emp.requestReason": "Request Reason",
  "emp.requestedAt": "Requested At",
  "emp.lastPasswordChange": "Last Password Change",
  "emp.lastLogin": "Last Sign-in",
  "emp.passwordRequestAction": "Password request…",
  "emp.stopRequiring": "Stop requiring new password",
  "emp.requireNew": "Require new password",
  "emp.noLongerRequired": "No longer required.",
  "emp.requiredNext": "Required at next sign-in.",
  "emp.deactivate": "Deactivate",
  "emp.activate": "Activate",
  "emp.deactivated": "Employee deactivated.",
  "emp.activated": "Employee activated.",
  "emp.updateFailed": "Could not update the employee.",
  "emp.reason": "Reason",
  "emp.requested": "Requested",
  "emp.reject": "Reject",
  "emp.approve": "Approve",
  "emp.sentToManager": "Password sent to the manager.",
  "emp.requestRejected": "Request rejected.",
  "emp.requestFailed": "Could not update the request.",
  "emp.create": "Create Employee",
  "emp.edit": "Edit Employee",
  "emp.lookingUp": "Looking up in SNR…",
  "emp.foundRole": "Found in SNR. HR title: {position} → {role}.",
  "emp.foundNoRole": "Found in SNR. HR title “{position}” matches no role; choose one.",
  "emp.snrFailed": "Could not look the employee up in SNR.",
  "emp.fillByHand": "Fill in the details by hand.",
  "emp.enterEmployeeNumber": "Enter Employee Number",
  "emp.enterName": "Enter Name",
  "emp.selectRole": "Select Role",
  "emp.selectClub": "Select Club",
  "emp.temporaryPassword": "Temporary Password",
  "emp.generate": "Generate",
  "emp.passwordHint": "They will be asked to choose their own on first sign-in.",
  "emp.clickToGenerate": "Click to generate password",
  "emp.selectManager": "Select Manager",
  "emp.addTemporaryClub": "Add Temporary Club",
  "emp.whyPosting": "Why the posting",
  "emp.startDate": "Start Date",
  "emp.endDate": "End Date",
  "emp.created": "Employee created.",
  "emp.updated": "Employee updated.",
  "emp.saveFailed": "Could not save the employee.",
  "emp.pictureUpdated": "Picture updated.",
  "emp.pictureRemoved": "Picture removed.",
  "emp.pictureFailed": "Could not update the picture.",

  "an.description": "Membership counts per club, straight off the members table.",
  "an.countedAt": "Counted {when}.",
  "an.notCounted": "Not counted yet; the worker counts on its next run.",
  "an.upToDate": "No member has changed since.",
  "an.recount": "Recount now",
  "an.recounting": "Recounting…",
  "an.recounted": "The figures are up to date.",
  "an.totalMembers": "Total members",
  "an.primary": "Primary",
  "an.supplementary": "Supplementary",
  "an.clubs": "Clubs",
  "an.searchClub": "Search club name",
  "an.business": "Business",
  "an.goldMember": "Gold Member",
  "an.affiliates": "Affiliates",
  "an.complimentary": "Complimentary",
  "an.employee": "Employee",
  "an.total": "Total",
  "an.clubName": "Club Name",
  "an.noClubs": "No clubs match.",
  "an.allClubs": "All clubs",
  "an.membersCount": "{n} members",
  "an.accountType": "Account Type",
  "an.sortBy": "Sort counts by",
  "an.both": "Both",

  "mem.singular": "Member",
  "mem.mr": "Mr",
  "mem.ms": "Ms",
  "mem.mrs": "Mrs",
  "mem.dr": "Dr",
  "mem.atty": "Atty",
  "mem.engr": "Engr",
  "mem.female": "Female",
  "mem.male": "Male",
  "mem.other": "Other",
  "mem.undisclosed": "Prefer not to say",
  "mem.walkIn": "Walk-in",
  "mem.canvassing": "Canvassing",
  "mem.benefits": "Benefits",
  "mem.business": "Business",
  "mem.gold": "Gold",
  "mem.unioil": "UniOil",
  "mem.qsr": "QSR",
  "mem.affiliates": "Affiliates",
  "mem.complimentary": "Complimentary",
  "mem.employee": "Employee",
  "mem.member": "Member",
  "mem.primary": "Primary",
  "mem.supplementary": "Supplementary",
  "mem.active": "Active",
  "mem.expired": "Expired",
  "mem.cancelled": "Cancelled",
  "mem.blacklisted": "Blacklisted",
  "mem.approved": "Approved",
  "mem.declined": "Declined",
  "mem.pending": "Pending",
  "mem.spouse": "Spouse",
  "mem.child": "Child",
  "mem.parent": "Parent",
  "mem.sibling": "Sibling",
  "mem.sectionIdentity": "Identity",
  "mem.sectionAccount": "Account",
  "mem.sectionCard": "Card",
  "mem.sectionClubsAddress": "Clubs & Address",
  "mem.sectionContactConsent": "Contact & Consent",
  "mem.sectionBusiness": "Business",
  "mem.sectionImages": "Images",
  "mem.prefix": "Prefix",
  "mem.suffix": "Suffix",
  "mem.firstName": "First Name",
  "mem.middleName": "Middle Name",
  "mem.lastName": "Last Name",
  "mem.name": "Name",
  "mem.hasLastName": "Has Last Name",
  "mem.gender": "Gender",
  "mem.dateOfBirth": "Date of Birth",
  "mem.accNo": "Acc. No.",
  "mem.snrAccountNumber": "S&R Account Number",
  "mem.accountType": "Account Type",
  "mem.accountStatus": "Account Status",
  "mem.membershipType": "Membership Type",
  "mem.membershipTier": "Membership Tier",
  "mem.acquisitionType": "Acquisition Type",
  "mem.memberSince": "Member Since",
  "mem.amendment": "Amendment",
  "mem.parentAccount": "Parent Account",
  "mem.supplementaryAccount": "Supplementary Account",
  "mem.hasSupplementaryCreated": "Has Supplementary Created",
  "mem.familyMember": "Family Member",
  "mem.employeeNumber": "Employee Number",
  "mem.employeeRelation": "Employee relation",
  "mem.volumeShopper": "Volume Shopper",
  "mem.coBrandTagging": "CO-Brand Tagging",
  "mem.cardName": "Card Name",
  "mem.cardNumber": "Card Number",
  "mem.temporaryCardNumber": "Temporary Card Number",
  "mem.issuedDate": "Issued Date",
  "mem.validUntil": "Valid Until",
  "mem.noExpiry": "No Expiry",
  "mem.isPrintReady": "Is Print Ready",
  "mem.temporarilyActive": "Temporarily Active",
  "mem.extendedTime": "Extended Time (Mins)",
  "mem.signupClub": "Sign-up Club",
  "mem.activeClub": "Active Club",
  "mem.renewClub": "Re-new Club",
  "mem.city": "City",
  "mem.barangay": "Barangay",
  "mem.addressStreet": "Address/Street",
  "mem.unitNumber": "Unit Number",
  "mem.buildingName": "Building Name",
  "mem.mobileNumber": "Mobile Number",
  "mem.email": "Email",
  "mem.phoneConsent": "Phone Consent",
  "mem.emailConsent": "Email Consent",
  "mem.onlyImportantUpdates": "Only Receive Important Updates",
  "mem.agreeTerm": "Agree Term",
  "mem.agreePersonalInfo": "Agree Personal Info",
  "mem.businessName": "Business Name",
  "mem.businessType": "Business Type",
  "mem.businessCompanyName": "Business Company Name",
  "mem.companyName": "Company Name",
  "mem.position": "Position",
  "mem.idCard": "ID card",
  "mem.profileImage": "Profile Image",
  "mem.advancedFilter": "Advanced filter",
  "mem.addMember": "Add Member",
  "mem.fullName": "Full Name",
  "mem.accountNumber": "Account Number",
  "mem.signUpClub": "Sign-Up Club",
  "mem.isEmailConsent": "Is Email Consent",
  "mem.isPhoneConsent": "Is Phone Consent",
  "mem.withEmail": "With Email",
  "mem.withoutEmail": "Without Email",
  "mem.withMobile": "With Mobile Number",
  "mem.withoutMobile": "Without Mobile Number",
  "mem.withTag": "With Tag",
  "mem.withoutTag": "Without Tag",
  "mem.createdBy": "Created By",
  "mem.createdByFilter": "Created by",
  "mem.modifiedBy": "Modified By",
  "mem.modifiedAt": "Modified At",
  "mem.memberN": "Member {id}",
  "mem.batch": "Batch",
  "mem.batchUpload": "Batch Upload",
  "mem.savedFilters": "Saved filters",
  "mem.filtersCount": "{count} filters",
  "mem.applied": "Applied",
  "mem.viewAllSaved": "View all {count}",
  "mem.savedFiltersHint": "Apply a set, or edit and delete the ones you keep.",
  "mem.editFilter": "Edit “{name}”",
  "mem.editName": "Edit {name}",
  "mem.saveChanges": "Save changes",
  "mem.noSavedFilters": "No saved filters yet",
  "mem.noSavedFiltersBody": "Set conditions in Advanced filter and choose Save set.",
  "mem.batchUpdate": "Batch Update",
  "mem.cancellation": "Cancellation",
  "mem.hintYesNo": "yes or no",
  "mem.hintCancellation":
    "amendment_cancellation says Cancellation; the reason is one of the configured ones",
  "mem.hintDate": "a date like 9/1/2026",
  "mem.hintEmployee": "the address, city, barangay, and Employee ID Number",
  "mem.hintAffiliates": "the Company Name",
  "mem.hintComplimentary": "the S&R Family Owner account number",
  "mem.templateHeaders": "the template's headers",
  "mem.hintUpload":
    "name, birth date, gender, contact, {extra}, then the consents as yes or no",
  "mem.csvDescription":
    "A CSV with the columns {columns} ({hint}). Keep number columns as text, or the spreadsheet rounds them. Up to 10,000 rows.",
  "mem.processing": "Processing",
  "mem.downloadTemplate": "Download sample template",
  "mem.csvFile": "CSV file",
  "mem.processFailed": "Could not process the file.",
  "mem.membersUpdated": "{count} members updated.",
  "mem.membersCreated": "{count} members created.",
  "mem.updatedCount": "{count} updated.",
  "mem.createdCount": "{count} created.",
  "mem.rowsSkipped": "{count} rows skipped.",
  "mem.rowN": "Row {n}: ",
  "mem.nothingApplied": "Nothing applied yet.",
  "mem.conditionsSet": "{count} conditions set.",
  "mem.saveSet": "Save set",
  "mem.reset": "Reset",
  "mem.apply": "Apply",
  "mem.mySavedFilters": "My saved filters",
  "mem.applyThisFilter": "Apply this filter",
  "mem.rename": "Rename",
  "mem.renameName": "Rename {name}",
  "mem.deleteName": "Delete {name}",
  "mem.someone": "someone",
  "mem.membershipSince": "Membership since",
  "mem.from": "From",
  "mem.to": "To",
  "mem.groupMemberStatus": "Member Status",
  "mem.groupCardInformation": "Card Information",
  "mem.groupAccountDetails": "Account Details",
  "mem.groupClubInformation": "Club Information",
  "mem.groupAddress": "Address",
  "mem.searchPlaceholder": "Name, account, S&R or card number…",
  "mem.exportTitle": "Export members",
  "mem.exportBody": "The screen holds {onScreen} of {total} matching members.",
  "mem.exportThisPage": "This page ({count})",
  "mem.exportEverything": "All {count}",
  "mem.exportEverythingUncounted": "All matching members",
  "mem.exportNote":
    "The server writes the rows as it reads them, so the file starts arriving before it is finished. Keep this open until it does.",
  "mem.exportFailed": "The export could not be prepared.",
  "mem.exportRows": "Members",
  "mem.exportColumns": "Columns",
  "mem.exportColumnCount": "{count} of {total} columns in the file.",
  "mem.exportSelectAll": "Select all",
  "mem.exportClearAll": "Clear all",
  "mem.exportFilters": "Filters",
  "mem.exportFilterCount": "{count} applied",
  "mem.exportNoFilters": "None applied",
  "mem.exportNeedsFilter":
    "Add at least one filter before exporting everything. Unfiltered this is every member on record, which is a file nobody can open and minutes of the server writing it.",
  "mem.exportRunningTitle": "Exporting members",
  "mem.exportRunningBody":
    "The file is being written and downloaded. This window stays until it is done.",
  "mem.exportElapsed": "{elapsed} elapsed",
  "mem.exportStop": "Cancel export",
  "mem.exportCancelled": "Export cancelled. Nothing was saved.",
  "mem.exportKeepOpen": "Closing this tab stops the download.",
  "mem.groupMembershipActivity": "Membership Activity",
  "mem.groupMembershipProfile": "Membership Profile",
  "mem.groupPermission": "Permission",
  "mem.groupSystemRelated": "System Related",
  "mem.presetAll": "All Members",
  "mem.presetActive": "All Active Members",
  "mem.presetActivePrimary": "All Active Primary",
  "mem.presetActiveSupplementary": "All Active Supplementary",
  "mem.presetCancelled": "All Cancelled",
  "mem.presetExpired": "All Expired",
  "mem.presetBlacklisted": "All Blacklisted",
  "mem.presetPendingToPrint": "Pending To Print",
  "mem.cardIssuedDate": "Card Issued Date",
  "mem.cardExpiryDate": "Card Expiry Date",
  "mem.volumeShopperTag": "Volume Shopper Tag",
  "mem.unionBankTag": "Union Bank Tag",
  "mem.cardReplacementHistory": "Card Replacement History",
  "mem.emailAddress": "Email Address",
  "mem.birthdate": "Birthdate",
  "mem.mobilePhoneConsent": "Mobile Phone Consent",
  "mem.createdDate": "Created Date",
  "mem.lastModifiedDate": "Last Modified Date",
  "mem.sourceOfApplication": "Source of Application",
  "mem.archived": "Archived",
  "mem.addRow": "Add",
  "mem.selectField": "Select field",
  "mem.all": "All",
  "mem.filterBy": "Filter by",
  "mem.removeFilter": "Remove filter",
  "mem.renameFilter": "Rename filter",
  "mem.saveThisFilter": "Save this filter",
  "mem.saveHint": "It will appear under My saved filters, ready to reapply.",
  "mem.filterSaved": "Filter saved.",
  "mem.filterSaveFailed": "Could not save the filter.",
  "mem.filterDeleted": "Filter deleted.",
  "mem.filterDeleteFailed": "Could not delete the filter.",
  "mem.renameFailed": "Could not rename the filter.",

  "md.upgrade": "Upgrade",
  "md.downgrade": "Downgrade",
  "md.cancellation": "Cancellation",
  "md.blacklist": "Blacklist",
  "md.cardReplacement": "Card Replacement",
  "md.photoUpdated": "Photo updated.",
  "md.photoRemoved": "Photo removed.",
  "md.photoFailed": "Could not update the photo.",
  "md.primaryUpdated": "Primary account updated.",
  "md.accountChangeFailed": "Could not change the account number.",
  "md.notFound": "Member not found",
  "md.notFoundBody": "This record may have been deleted, or the link may be out of date.",
  "md.backToMembers": "Back to members",
  "md.accountNumber": "Account Number:",
  "md.print": "Print",
  "md.amendName.blacklist": "Blacklist",
  "md.amendName.cancellation": "Cancellation",
  "md.amendName.card-replacement": "Card replacement",
  "md.amendName.upgrade": "Upgrade to primary",
  "md.amendName.downgrade": "Downgrade to supplementary",
  "md.amendName.change-tier": "Change tier",
  "md.amendName.activate": "Activate",
  "md.amendName.account-renew": "Account renew",
  "md.amendName.change-primary-account": "Change primary account",
  "md.amend.blacklist":
    "Blocks the account. A primary's active supplementaries are cancelled with it.",
  "md.amend.cancellation":
    "Ends the membership. A primary's active supplementaries end with it.",
  "md.amend.card-replacement":
    "Same account, new plastic: the card number's last two digits go up by one and a print is queued.",
  "md.amend.upgrade":
    "The supplementary becomes a primary of its own on a fresh card, active for a year.",
  "md.amend.downgrade":
    "The member becomes a supplementary of the primary named, on a card under that account.",
  "md.amend.change-tier":
    "A fresh card in the new tier's digits and a year's validity. The address must be on file.",
  "md.amend.activate": "Active again for a year on a reissued card.",
  "md.amend.account-renew":
    "Lets the expired card shop for ten minutes; a renewal at the till inside that window keeps it active.",
  "md.amend.change-primary-account":
    "Moves the supplementary under a different primary, on a card under that account.",
  "md.changeTier": "Change tier",
  "md.activate": "Activate",
  "md.accountRenew": "Account renew",
  "md.changePrimaryAccount": "Change primary account",
  "md.apply": "Apply",
  "md.amendmentApplied": "Amendment applied.",
  "md.amendmentFailed": "Could not apply the amendment.",
  "md.noAmendments": "No amendment available",
  "md.selectReason": "Select a reason",
  "md.others": "Others",
  "md.specifyReason": "Specify the reason",
  "md.reasonRequired": "A reason is required.",
  "md.employeeNumberRequired": "An employee membership needs the employee number.",
  "md.primaryRequired": "The primary's account number is required.",
  "md.primaryAccountHint": "Account or card number of the primary",
  "md.printCard": "Print card",
  "md.printBody": "Records the print, then opens the print dialog.",
  "md.printReason": "Print reason",
  "md.printReasonHint": "Why the card is printed",
  "md.printNew": "New card",
  "md.printRenew": "Renewal",
  "md.printLost": "Lost card",
  "md.printDamaged": "Damaged card",
  "md.printFailed": "Could not record the print.",
  "md.membersConsent": "Members Consent",
  "md.onlyImportantUpdates": "Only important updates",
  "md.email": "Email",
  "md.phone": "Phone",
  "md.na": "N/A",
  "md.cardInformation": "Card Information",
  "md.validUntil": "Valid until",
  "md.basicInformation": "Basic Information",
  "md.attachments": "Attachments",
  "md.selectAmendment": "Select Amendment",
  "md.primaryAccountNo": "Primary Account No.",
  "md.changeAccountNo": "Change account no.",
  "md.coBrandTagging": "Co-Brand Tagging",
  "md.coBrand": "Co-Brand",
  "md.changePrimaryTitle": "Change primary account number",
  "md.changePrimaryBody": "This links the supplementary record to a different primary account.",
  "md.primaryAccountNumber": "Primary account number",
  "md.hide": "Hide",
  "md.show": "Show",

  "hist.details": "Details",
  "hist.cardHistory": "Card History",
  "hist.acquisitionDetails": "Acquisition Details",
  "hist.acquisitionDetail": "Acquisition Detail",
  "hist.printingHistory": "Printing History",
  "hist.print": "Print",
  "hist.transactionHistory": "Transaction History",
  "hist.cardTransaction": "Card Transaction",
  "hist.amendmentHistory": "Amendment History",
  "hist.amendmentDetail": "Amendment Detail",
  "hist.primaryTable": "Primary Table",
  "hist.supplementaryHistory": "Supplementary History",
  "hist.attachment": "Attachment",
  "hist.cardNoFrom": "Card No. From",
  "hist.cardNumberTo": "Card Number To",
  "hist.cardNoTo": "Card No. To",
  "hist.description": "Description",
  "hist.amendmentDateTime": "Amendment Date / Time",
  "hist.createdBy": "Created By",
  "hist.noOfYears": "No. Of Years",
  "hist.transactionNo": "Transaction No",
  "hist.paymentPurpose": "Payment Purpose",
  "hist.amount": "Amount",
  "hist.paymentType": "Payment Type",
  "hist.transactionDate": "Transaction Date",
  "hist.operatorNumber": "Operator Number",
  "hist.printType": "Print Type",
  "hist.printReason": "Print Reason",
  "hist.printStatus": "Print Status",
  "hist.datePrinted": "Date Printed",
  "hist.cardNumber": "Card Number",
  "hist.additionalReason": "Additional Reason",
  "hist.cardReplacement": "Card Replacement",
  "hist.newCard": "New Card",
  "hist.damaged": "Damaged",
  "hist.renew": "Renew",
  "hist.new": "New",
  "hist.lost": "Lost",
  "hist.others": "Others",
  "hist.activate": "Activate",
  "hist.upgrade": "Upgrade",
  "hist.downgrade": "Downgrade",
  "hist.pending": "Pending",
  "hist.printed": "Printed",
  "hist.failed": "Failed",
  "hist.transactionNumber": "Transaction Number",
  "hist.modeOfPayment": "Mode Of Payment",
  "hist.years": "Years",
  "hist.renewClub": "Renew Club",
  "hist.reverted": "Reverted",
  "hist.registerNumber": "Register Number",
  "hist.numberOfYears": "Number Of Years",
  "hist.isReverted": "Is Transaction Reverted",
  "hist.amendmentType": "Amendment Type",
  "hist.reason": "Reason",
  "hist.oldAccountStatus": "Old Account Status",
  "hist.date": "Date",
  "hist.isPrinted": "Is Printed",
  "hist.name": "Name",
  "hist.validTill": "Valid Till",
  "hist.parentName": "Parent Name",
  "hist.parentCardNumber": "Parent Card Number",
  "hist.parentValidTill": "Parent Valid Till",
  "hist.title": "Title",
  "hist.added": "Added",
  "hist.addedBy": "Added By",
  "hist.file": "File",
  "hist.deleted": "{singular} deleted.",
  "hist.deleteFailed": "Could not delete the {singular}.",
  "hist.download": "Download",
  "hist.noneYet": "No {section} yet",
  "hist.noneBody": "Anything recorded against this member's {section} will be listed here.",
  "hist.previous": "Previous",
  "hist.next": "Next",
  "hist.perPage": "{n} / page",
  "hist.confirmDeleteBody":
    "This removes the entry from the member's record. It cannot be undone.",

  "new.tagline": "Add a new Member to the SNR family",
  "new.whoToAdd": "Who would you like to add?",
  "new.member": "Member",
  "new.affiliatesMember": "Affiliates Member",
  "new.employeeMember": "Employee Member",
  "new.complimentaryMember": "Complimentary Member",
  "new.enrolled": "{name} enrolled as {number}.",
  "new.enrolFailed": "Could not enrol the member.",
  "new.contactRequired": "Please provide at least one contact method (Mobile Number or Email).",
  "new.consentsRequired": "Both consents are needed to continue.",
  "new.tooYoung": "A member must be at least 18 years old.",
  "new.mobileRequired": "A mobile number is needed to send the updates consented to.",
  "new.emailRequired": "An email address is needed to send the updates consented to.",
  "new.selectMembership": "Select Membership",
  "new.enterEmployeeNumber": "Enter Employee ID Number",
  "new.enterCompanyName": "Enter Company Name",
  "new.familyOwner": "S&R Family Owner",
  "new.inheritedFromPrimary":
    "The tier, membership and card validity are taken from the primary account.",
  "new.idCard": "ID Card",
  "new.scanId": "Scan ID",
  "new.photo": "Photo",
  "new.capturePhoto": "Capture photo",
  "new.autoFill": "These fill in automatically if you scan the ID card first.",
  "new.selectAcquisition": "Select Acquisition",
  "new.primaryAccountNumber": "Primary Account Number",
  "new.primaryPlaceholder": "The primary member's account number",
  "new.selectPrefix": "Select prefix",
  "new.selectSuffix": "Select suffix",
  "new.enterFirstName": "ENTER FIRST NAME",
  "new.enterMiddleName": "ENTER MIDDLE NAME",
  "new.toggleNoLastName": "toggle off if no last name",
  "new.enterLastName": "ENTER LAST NAME",
  "new.selectGender": "Select gender",
  "new.enterEmail": "Enter email address",
  "new.contactNote": "Please provide at least one contact method (Mobile Number or Email)",
  "new.enterUnitNumber": "Enter unit number",
  "new.enterBuildingName": "Enter building name",
  "new.enterAddress": "Enter address/street",
  "new.selectCity": "Select city",
  "new.selectBarangay": "Select barangay",
  "new.selectCityFirst": "Select city first",
  "new.receiveUpdates": "Receive Updates",
  "new.receiveBody":
    "I would like to receive updates on exclusive offer, promotions and latest information about new products and services from S&R Membership Shopping through the following:",
  "new.privacyConsent": "Privacy and Consent",
  "new.agreeTerms": "I have read and agree to the",
  "new.termsAndConditions": "terms and conditions",
  "new.and": "and",
  "new.privacyPolicy": "privacy policy",
  "new.agreePersonal":
    "I hereby agree and consent that S&R Membership Shopping may use my personal information for the",
  "new.statedReasons": "stated reasons",
  "new.saving": "Saving",
  "new.clickToUpload": "Click to Upload",
  "new.orDrag": "or drag and drop",
  "new.pngOrJpg": "PNG or JPG",
  "new.primaryMember": "Primary Member",
  "new.primaryGateBody":
    "Enter the account number of the primary member this supplementary card will be issued under.",
  "new.lookingUp": "Looking up the account\u2026",
  "new.primaryFound": "Account found and eligible.",
  "new.primaryNotFound": "This account cannot take a supplementary card.",
  "new.inheritsFromPrimary":
    "This card will be a {tier} membership, taken from the primary. {used} of 2 supplementary cards are active under it.",
  "new.verifyDetails": "Verify details",
  "new.verifyBody":
    "The card will be issued under this account. Please confirm before continuing.",
  "new.employeeGateBody":
    "The employee number is checked against the members already on file before the form opens.",
  "new.employeeTaken": "That employee number is already on another member.",
  "new.duplicateMember": "A member with this name and date of birth already exists.",
  "new.temporaryCardDigits": "Eleven digits",
  "new.employeeCheckFailed": "Could not check the employee number. Please try again.",
  "new.checking": "Checking",
  "new.approvedBy": "Approved By",
  "new.selectApprovedBy": "Select who approved this",
  "new.enterTemporaryCard": "Enter the temporary card number",
  "new.walkInNeedsPictures": "A walk-in enrolment needs the ID card and the photograph.",
  "new.canvassingNeedsCard": "A canvassing enrolment needs the temporary card number.",
  "common.back": "Back",
  "common.confirm": "Confirm",

  "roles.model.member": "Member",
  "roles.model.memberNote": "Member note",
  "roles.model.memberAttachment": "Member attachment",
  "roles.model.memberLog": "Member log",
  "roles.model.memberColumnSetting": "Member column setting",
  "roles.model.memberSavedFilter": "Member saved filter",
  "roles.model.cardHistory": "Card history",
  "roles.model.cardTransaction": "Card transaction",
  "roles.model.printHistory": "Print history",
  "roles.model.acquisitionDetail": "Acquisition detail",
  "roles.model.amendmentDetail": "Amendment detail",
  "roles.model.supplementaryHistory": "Supplementary history",
  "roles.model.nonMember": "Non member",
  "roles.model.accessPolicySetting": "Access policy setting",
  "roles.model.employeeActivityLog": "Employee activity log",
  "roles.model.organization": "Organization",
  "roles.model.province": "Province",
  "roles.model.city": "City",
  "roles.model.barangay": "Barangay",
  "roles.model.club": "Club",
  "roles.model.yellowPage": "Yellow page",
  "roles.model.yellowPageCategory": "Yellow page category",
  "roles.model.promoCode": "Promo code",
  "roles.model.reason": "Reason",
  "roles.model.businessType": "Business type",
  // The member detail page: one card at a time.
  "md.businessInformation": "Business Information",
  "md.viewMore": "View more",
  "md.viewLess": "View less",
  "md.cardStatus": "Card Status",
  "md.volumeShopperOn": "Volume Shopper: Enabled",
  "md.volumeShopperOff": "Volume Shopper: Disabled",
  "md.volumeShopperBody":
    "A volume shopper is a member flagged as a high-volume buyer. Update the tag below.",
  "md.enableVolumeShopper": "Enable Volume Shopper",
  "md.disableVolumeShopper": "Disable Volume Shopper",
  "md.volumeShopperUpdated": "Volume shopper updated.",
  "md.updateFailed": "Could not save the change.",
  "md.printNeedsPhoto": "Print is disabled until a photo is on file",
  "md.printNotActive": "Cannot print: the account is {status}",
  "md.printTemporary": "Cannot print: the card is only renewed for 10 minutes",
  "md.supplementaryAccount": "Supplementary Account",
  "md.addSupplementary": "Add Supplementary",
  "md.hasSupplementary": "A supplementary card hangs off this account.",
  "md.noSupplementary": "No supplementary card yet.",
  "md.noPermission": "You don't have permission",
  "hist.notes": "Notes",
  "hist.note": "Note",
  "hist.club": "Club",
  "hist.leftAt": "Left At",
  "hist.promoCode": "Promo Code",
  "hist.complaintReason": "Complaint Reason",
  "hist.requestedBy": "Requested By",
  "hist.endorsedBy": "Endorsed By",
  "hist.approvedBy": "Approved By",
  "new.taglineFor": "Add a new {tier} to the SNR family",
  "new.removePicture": "Remove picture",
  "new.captureGuide": "Capture guide",
  "new.guide1": "Make sure you are the person who is applying.",
  "new.guide2": "Ensure you are in a well-lit area.",
  "new.guide3": "Position your face within the frame.",
  "new.guide4": "Ensure your face is directly visible with no side angles.",
} as const;

export type TranslationKey = keyof typeof en;
export type Translate = (key: TranslationKey) => string;
type Dictionary = Record<TranslationKey, string>;

const fil: Dictionary = {
  "login.welcome": "Maligayang pagbabalik sa MRM",
  "login.subtitle": "Ilagay ang iyong employee ID at password para mag-sign in.",
  "login.employeeId": "Employee ID",
  "login.employeeIdPlaceholder": "hal. 12345678",
  "login.password": "Password",
  "login.passwordPlaceholder": "Ilagay ang iyong password",
  "login.showPassword": "Ipakita ang password",
  "login.hidePassword": "Itago ang password",
  "login.resetPassword": "I-reset ang password?",
  "login.continue": "Magpatuloy",
  "login.signingIn": "Nagsa-sign in…",
  "login.errorGeneric": "Hindi ka namin ma-sign in.",
  "login.errorEmployeeIdRequired": "Ilagay ang iyong employee ID",
  "login.errorPasswordRequired": "Ilagay ang iyong password",
  "login.mustChangePassword": "Pumili ng bagong password bago magpatuloy.",
  "login.lockedTitle": "Masyadong maraming pagsubok mag-sign in",
  "login.lockedBody":
    "Para sa iyong seguridad, pansamantalang nakahinto ang pag-sign in sa device na ito. Maghintay ng {time} at subukan muli. Walang problema sa iyong account.",
  "login.lockedAction": "Subukan muli sa {time}",
  "login.hintBoth": "Ilagay ang iyong employee ID at password para magpatuloy.",
  "login.hintId": "Ilagay ang iyong employee ID — hindi bababa sa {n} karakter.",
  "login.hintPassword": "Ilagay ang iyong password — hindi bababa sa {n} karakter.",

  "reset.title": "Maligayang pagbabalik sa MRM",
  "reset.subtitle": "Ilagay ang Employee ID at ang dahilan ng password reset.",
  "reset.employeeIdPlaceholder": "Ilagay ang employee id",
  "reset.reason": "Dahilan ng Kahilingan",
  "reset.reasonPlaceholder": "Pumili ng dahilan",
  "reset.errorReasonRequired": "Pumili ng dahilan",
  "reset.submit": "I-reset ang Password",
  "reset.submitting": "Ipinapadala…",
  "reset.back": "Bumalik sa Sign In",
  "pw.backHome": "Bumalik sa home",
  "reset.sentTitle": "Pag-reset ng Password",
  "reset.sentBody":
    "Naipadala na ang iyong kahilingan. Aabisuhan ka ng iyong department head kapag handa na ang bagong pansamantalang password para sa pag-login.",
  "reset.sentDismiss": "Isara",
  "reset.errorGeneric": "Hindi naipadala ang iyong kahilingan.",
  "reset.expiredSubtitle":
    "Nag-expire na ang iyong password. Magpadala ng kahilingan sa pag-reset para magpatuloy.",
  "reset.lockedSubtitle":
    "Naka-lock ang account na ito dahil sa sobrang maling pagsubok. Magpadala ng kahilingan sa pag-reset para ma-unlock ito.",

  "showcase.title": "Pamamahala ng Membership",
  "showcase.subtitle":
    "Pamahalaan ang impormasyon ng miyembro, access level, at aktibidad sa isang lugar.",
  "showcase.pwTitle": "Oras na para pumili ng bago",
  "showcase.pwFirstTitle": "Isang huling hakbang",
  "showcase.pwFirstBody":
    "Ang password na ginamit mo sa pag-sign in ay ibinigay sa iyo. Pumili ng sarili mong alam lang.",
  "showcase.pwIdle": "Panatilihing sa iyo ang iyong account",
  "showcase.pwBody":
    "Kapag nagtakda ka ng bagong password, mala-log out ang lahat ng ibang session.",

  "nav.dashboard": "Pangunahing Panel",
  "nav.members": "Mga Miyembro",
  "nav.memberAnalytics": "Analitika ng Miyembro",
  "nav.nonMembers": "Hindi Miyembro",
  "nav.employees": "Empleyado",
  "nav.locationConfig": "Master Config ng Lokasyon",
  "nav.provinces": "Mga Probinsya",
  "nav.cities": "Lungsod",
  "nav.barangays": "Barangay",
  "nav.maps": "Mga Mapa",
  "nav.memberPopulation": "Miyembro at Populasyon",
  "map.points": "mga punto sa mapa",
  "map.subtitle": "Nasaan ang mga miyembro, at nasaan ang mga tao, sa isang mapa.",
  "map.held":
    "Nakapirmi muna ang mapa hanggang dumating ang mga ito — kung igagalaw ngayon, uulitin lang ang paghingi.",
  "map.stop": "Itigil ang pagkarga",
  "map.stopped": "Itinigil. Igalaw ang mapa para subukan ulit.",
  "map.cacheDate": "Petsa ng cache: {time}",
  "map.expiresAt": "mag-e-expire nang {time}",
  "map.cached": "naka-cache — i-refresh para sa pinakabago",
  "map.refreshFailed": "Hindi na-refresh ang mapa.",
  "map.populationByClub": "Populasyon ayon sa Club",
  "map.philippinesPopulation": "Populasyon ng Pilipinas",
  "map.members": "Miyembro ng S&R",
  "map.view": "Uri ng mapa",
  "map.allClubs": "Lahat ng club",
  "map.clubsSelected": "{count} club ang napili",
  "map.all": "Lahat",
  "map.population": "populasyon",
  "map.noPopulation": "Walang nakatalang populasyon",
  "map.membersUnit": "miyembro",
  "map.level.province": "Mga probinsya — mag-zoom in para sa mga lungsod",
  "map.level.city": "Mga lungsod — mag-zoom in para sa mga barangay",
  "map.level.barangay": "Mga barangay sa view",
  "map.inView": "sa view",
  "map.level.memberClubs":
    "Mga miyembro bawat club — mag-zoom in para makita kung saan sila nakatira",
  "map.level.memberBarangays": "Mga miyembro ayon sa barangay, para sa mga napiling club",
  "map.unit.province": "probinsya",
  "map.unit.city": "lungsod",
  "map.unit.barangay": "barangay",
  "map.unit.club": "club",
  "map.level.clubs":
    "Mga club, kasama ang populasyon ng lungsod — mag-zoom in para sa mga barangay",
  "nav.yellowPage": "Yellow Page",
  "nav.configuration": "Konpigurasyon",
  "nav.group.overview": "Pangkalahatan",
  "nav.group.membership": "Pagkamiyembro",
  "nav.group.directory": "Direktoryo",
  "nav.group.geography": "Heograpiya",
  "nav.group.system": "Sistema",
  "page.activityLog": "Activity Log",
  "page.memberActivityLog": "Activity Log ng Miyembro",
  "page.memberDetail": "Detalyadong View",
  "page.yellowPageCategories": "Mga Kategorya ng Yellow Page",
  "page.profile": "Aking Profile",
  "page.changePassword": "Palitan ang Password",
  "shell.home": "Tahanan",
  "shell.system": "Sistemang MRM",
  "shell.search": "Maghanap...",
  "shell.collapse": "Itupi ang sidebar",
  "shell.openMenu": "Buksan ang menu",
  "shell.closeMenu": "Isara ang menu",
  "shell.expand": "Buksan ang sidebar",
  "shell.roleSuperuser": "Superuser",
  "shell.roleAdmin": "Admin",
  "shell.roleMember": "Miyembro",
  "shell.editProfile": "I-edit ang Profile",
  "shell.profilePicture": "Larawan sa Profile",
  "shell.pictureUpdated": "Na-update ang larawan sa profile.",
  "shell.pictureFailed": "Hindi ma-update ang iyong larawan.",
  "shell.logout": "Mag-logout",
  "shell.viewProfile": "Tingnan ang profile",
  "shell.changePassword": "Palitan ang password",
  "shell.language": "Wika",
  "shell.theme": "Tema",
  "shell.accountMenu": "Menu ng account",
  "shell.navSearch": "Salain ang menu",
  "shell.noNavMatch": "Walang tugmang item sa menu",
  "shell.mainNav": "Pangunahing nabigasyon",
  "shell.skipToContent": "Lumaktaw sa nilalaman",
  "profile.subtitle": "Ang iyong account, ayon sa HR at sa sistema.",
  "profile.editProfile": "I-edit ang profile",
  "profile.changePicture": "Palitan ang larawan",
  "profile.removePicture": "Alisin ang larawan",
  "profile.cancel": "Kanselahin",
  "profile.save": "I-save ang mga pagbabago",
  "profile.saving": "Sine-save…",
  "profile.fullName": "Buong pangalan",
  "profile.email": "Email address",
  "profile.emailHint": "Dito ipinadadala ang mga pansamantalang password at abiso.",
  "profile.employeeId": "Employee ID",
  "profile.employeeIdHint": "Itinalaga ng HR; hindi mababago dito.",
  "profile.club": "Pangunahing club",
  "profile.noClub": "Walang nakatalagang club",
  "profile.roles": "Mga tungkulin",
  "profile.status": "Katayuan ng account",
  "profile.lastLogin": "Huling pag-sign in",
  "profile.passwordChanged": "Huling pagpalit ng password",
  "profile.created": "Nilikha ang account",
  "profile.never": "Wala pa",
  "profile.errorName": "Ilagay ang iyong pangalan.",
  "profile.errorEmail": "Ilagay ang wastong email address.",
  "profile.updated": "Na-update ang profile.",
  "profile.saveFailed": "Hindi ma-save ang iyong profile.",
  "profile.pictureRemoved": "Naalis ang larawan.",
  "profile.sectionAccount": "Account",
  "profile.sectionPosting": "Pansamantalang pagtalaga",
  "profile.sectionSecurity": "Seguridad",
  "profile.postingNone": "Walang nakatalang pansamantalang pagtalaga.",
  "profile.postingActive": "Aktibo ngayon",
  "profile.postingScheduled": "Hindi pa umiiral ngayon",
  "profile.postingClub": "Nakatalaga sa",
  "profile.postingFrom": "Mula",
  "profile.postingUntil": "Hanggang",
  "profile.postingReason": "Dahilan",
  "profile.superuser": "Superuser — lahat ng pahintulot",
  "profile.forceChange": "Kinakailangang palitan ang password",
  "profile.forceChangeYes": "Oo, bago ang iba pa",
  "profile.forceChangeNo": "Hindi",
  "profile.openRequest": "Nakabinbing kahilingan sa password",
  "profile.openRequestNone": "Wala",
  "profile.updated_at": "Huling na-update",
  "profile.permissions": "Mga pahintulot",
  "profile.permissionsCount": "{count} ang ipinagkaloob",
  "status.active": "Aktibo",
  "status.password_expired": "Paso na ang password",
  "status.account_locked": "Naka-lock",
  "status.inactive": "Hindi aktibo",
  "status.deleted": "Tinanggal",

  "dash.kicker": "Lahat ng iyong pinamamahalaan, nasa isang lugar.",
  "dash.title": "Pangunahing Panel",
  "dash.statMembers": "Mga Miyembro",
  "dash.statArchived": "Mga naka-archive na miyembro",
  "dash.statArchivedHint": "Tinanggal, ngunit itinago para sa talaan",
  "dash.statNonMembers": "Hindi Miyembro",
  "dash.statRecent": "{n} sa nakaraang 30 araw",
  "dash.statPromoCodes": "Aktibong promo code",
  "dash.statExpired": "{n} ang paso na",
  "dash.panelMembers": "Pagkamiyembro",
  "dash.byStatus": "Ayon sa status",
  "dash.byTier": "Ayon sa tier",
  "dash.byAccountType": "Ayon sa uri ng account",
  "dash.nothingYet": "Wala pang mabibilang.",
  "dash.location": "Lokasyon at Mga Log",
  "dash.locationText": "Master config at mga audit trail",
  "dash.workspace": "Lugar ng Trabaho",
  "dash.workspaceText": "Tuklasin ang iyong mga module",
  "dash.addMember": "Magdagdag ng Miyembro",
  "dash.addMemberText": "Mag-enroll ng bagong miyembro",
  "dash.members": "Mga Miyembro",
  "dash.membersText": "Tingnan ang listahan ng mga miyembro",
  "dash.employees": "Mga Empleyado",
  "dash.employeesText": "Pamahalaan ang mga tala ng staff",
  "dash.nonMember": "Hindi Miyembro",
  "dash.nonMemberText": "Irehistro ang isang prospect",
  "dash.province": "Probinsya",
  "dash.provinceText": "Pamahalaan ang mga probinsya",
  "dash.city": "Lungsod / Lokasyon",
  "dash.cityText": "Pamahalaan ang mga lungsod at lugar",
  "dash.barangay": "Barangay",
  "dash.barangayText": "Pamahalaan ang mga barangay",
  "dash.employeeLog": "Activity Log ng Empleyado",
  "dash.employeeLogText": "Audit trail para sa staff",
  "dash.memberLog": "Activity Log ng Miyembro",
  "dash.memberLogText": "Audit trail para sa mga miyembro",
  "dash.yellowPage": "Yellow Page",
  "dash.yellowPageText": "Direktoryo ng mga negosyo",
  "dash.maps": "Mga Mapa",
  "dash.mapsText": "Saklaw at populasyon",
  "dash.configuration": "Konpigurasyon",
  "dash.configurationText": "Mga setting ng sistema",
  "dash.restricted": "Hindi bukas para sa iyong tungkulin",
  "dash.restrictedHint": "Hindi kasama sa iyong tungkulin ang access sa screen na ito.",

  "pw.title": "Palitan ang password",
  "pw.forced":
    "Palitan ang pansamantalang password na ibinigay sa iyo ng sarili mong alam lang.",
  "pw.noExpiry":
    "Hindi nag-e-expire ang iyong password, ngunit maaari mo itong palitan anumang oras.",
  "idle.warnTitle": "Nariyan ka pa ba?",
  "idle.warnBody": "Wala nang aktibidad kaya mala-log out ka sa loob ng {n} segundo.",
  "idle.stay": "Nandito pa ako",
  "idle.signedOut":
    "Na-sign out ka pagkatapos ng {n} minutong walang aktibidad. Mag-sign in para magpatuloy.",
  "pw.expired": "Paso na ang iyong password. Palitan ito upang makapag-sign in pa.",
  "pw.today": "Mag-e-expire ang iyong password ngayong araw.",
  "pw.inDays": "Mag-e-expire ang iyong password sa loob ng {days} araw.",
  "pw.inOneDay": "Mag-e-expire ang iyong password bukas.",
  "pw.current": "Kasalukuyang password",
  "pw.new": "Bagong password",
  "pw.confirm": "Kumpirmahin ang bagong password",
  "pw.errorCurrent": "Ilagay ang iyong kasalukuyang password",
  "pw.ruleLength": "8 hanggang 20 karakter",
  "pw.ruleLower": "Maliit na titik",
  "pw.ruleUpper": "Malaking titik",
  "pw.ruleDigit": "Numero",
  "pw.ruleSymbol": "Simbolo",
  "pw.ruleDifferent": "Iba sa iyong kasalukuyang password",
  "pw.ruleMatch": "Magkatugma ang dalawang bagong password",
  "pw.submit": "Itakda ang Bagong Password",
  "pw.subtitle": "Maglagay ng bagong password para sa iyong account",
  "pw.requirements": "Mga Kailangan sa Password",
  "pw.saving": "Sine-save…",
  "pw.success": "Na-update ang password.",
  "pw.failed": "Hindi mapalitan ang iyong password.",
  "pw.tokenExpired":
    "Nag-time out ang iyong pag-sign in. Mag-sign in muli para itakda ang password.",
  "pw.expiresIn":
    "Mag-e-expire ang page na ito sa loob ng {time}. Pagkatapos nito ay kailangan mong mag-sign in muli.",

  "common.edit": "I-edit",
  "common.delete": "Tanggalin",
  "common.cancel": "Kanselahin",
  "common.create": "Likhain",
  "common.submit": "Isumite",
  "common.saving": "Sine-save…",
  "common.loading": "Naglo-load…",
  "common.loadMore": "Magpakita pa",
  "common.close": "Isara",
  "common.deleted": "Natanggal.",
  "common.created": "Nalikha.",
  "common.updated": "Na-update.",
  "common.saved": "Na-save.",
  "common.couldNotDelete": "Hindi matanggal.",
  "common.staleRecord":
    "Binago ng iba ang record na ito. Na-reload na; pakiulit ang iyong pagbabago.",
  "common.recordBusy":
    "May ibang nagse-save ng record na ito. Maghintay sandali at subukang muli.",
  "common.couldNotSave": "Hindi ma-save.",
  "common.nothingYet": "Wala pa.",
  "common.shownOfTotal": "{shown} sa {total}",
  "common.confirmDelete": "Tanggalin ang {name}? Hindi na ito maibabalik.",
  "common.confirmDeleteTitle": "Tanggalin ang {model}",
  "common.charactersRemaining": "{count} karakter ang natitira",
  "common.comingSoon": "Malapit na",
  "common.onItsWay": "Paparating na ang {section}.",
  "common.of": "sa",
  "common.showing": "Ipinapakita",
  "common.rowsPerPage": "Mga row bawat pahina",
  "common.firstPage": "Unang pahina",
  "common.previousPage": "Nakaraang pahina",
  "common.nextPage": "Susunod na pahina",
  "common.lastPage": "Huling pahina",
  "cfg.subtitle": "Ang mga listahan, limitasyon at tungkulin na pinagbabatayan ng sistema.",
  "cfg.title": "Mga Configuration",
  "cfg.notes": "Mga Tala",
  "cfg.reasons": "Mga Dahilan",
  "cfg.accessPolicy": "Patakaran sa pag-access",
  "cfg.passwordExpiry": "Pag-expire ng Password",
  "cfg.promoCodes": "Mga Promo Code",
  "cfg.businessType": "Uri ng Negosyo",
  "cfg.businessUnit": "Yunit ng Negosyo",
  "cfg.roles": "Mga Tungkulin",
  "cfg.preOpening": "Bago ang Pagbubukas",
  "cfg.complimentary": "Komplimentaryo",
  "cfg.endorsement": "Pag-endorso",
  "cfg.notesText": "I-configure ang mga tala para sa pre-opening, complimentary at endorsement",
  "cfg.requestedBy": "Hiniling Ni",
  "cfg.idleTitle": "Oras ng Pagkatigil",
  "cfg.minutes": "Minuto",
  "cfg.days": "Araw",
  "cfg.businessTypeText":
    "I-configure ang listahan ng mga uri ng negosyo para sa pagrerehistro at pag-uuri ng miyembro",
  "cfg.businessUnitText":
    "I-configure ang listahan ng mga club para sa pamamahala ng business unit",
  "cfg.businessTypeField": "Uri ng negosyo",
  "cfg.currentBusinessTypes": "Kasalukuyang mga uri ng negosyo",
  "cfg.addBusinessType": "Magdagdag ng uri ng negosyo",
  "cfg.businessTypeDeleted": "Natanggal ang uri ng negosyo.",
  "cfg.businessTypeDeleteFailed": "Hindi matanggal ang uri ng negosyo.",
  "cfg.noBusinessTypes": "Wala pang uri ng negosyo.",
  "cfg.club": "Club",
  "cfg.clubName": "Pangalan ng club",
  "cfg.clubCode": "Code ng club",
  "cfg.city": "Lungsod",
  "cfg.address": "Adres",
  "cfg.latitude": "Latitud",
  "cfg.longitude": "Longhitud",
  "cfg.addClub": "Magdagdag ng club",
  "cfg.clubDeleted": "Natanggal ang club.",
  "cfg.clubDeleteFailed": "Hindi matanggal ang club.",
  "cfg.noClubs": "Wala pang club.",
  "roles.intro":
    "Ang tungkulin ay hanay ng mga bagay na maaaring gawin ng isang tao. Bigyan siya ng tungkulin at magagawa niya ang lahat ng nasa loob nito.",
  "roles.role": "Tungkulin",
  "roles.addRole": "Magdagdag ng tungkulin",
  "roles.builtIn": "Nakapaloob",
  "roles.builtInName":
    "Hindi mapapalitan ang pangalan ng nakalaang tungkulin; mababago pa rin ang mga pahintulot nito.",
  "roles.builtInNoDelete": "Hindi maaaring tanggalin ang mga built-in na tungkulin",
  "roles.confirmDeleteTitle": "Tanggalin ang tungkulin",
  "roles.confirmDelete":
    'Tanggalin ang tungkuling "{name}"? Mawawala ang mga pahintulot nito sa mga taong nasa loob.',
  "roles.none": "Wala pang tungkulin.",
  "roles.deleted": "Natanggal ang tungkulin.",
  "roles.deleteFailed": "Hindi matanggal ang tungkulin.",
  "roles.created": "Nalikha ang tungkulin.",
  "roles.updated": "Na-update ang tungkulin.",
  "roles.saveFailed": "Hindi ma-save ang tungkulin.",
  "roles.back": "Bumalik sa mga tungkulin",
  "roles.new": "Bagong tungkulin",
  "roles.edit": "I-edit ang tungkulin",
  "roles.tickIntro":
    "Markahan ang maaaring gawin ng mga taong may ganitong tungkulin. Bawat hanay ay uri ng tala; bawat kolum ay kilos.",
  "roles.name": "Pangalan ng tungkulin",
  "roles.namePlaceholder": "hal. Club Manager",
  "roles.description": "Paglalarawan",
  "roles.descriptionPlaceholder": "hal. Namamahala sa isang club araw-araw",
  "roles.groupMembers": "Mga Miyembro",
  "roles.groupPeople": "Mga Tao at Access",
  "roles.groupGeography": "Heograpiya",
  "roles.groupCatalogue": "Katalogo at Setting",
  "roles.groupCount": "{selected} sa {total}",
  "roles.details": "Mga Detalye",
  "roles.permissions": "Mga Pahintulot",
  "roles.quickStart": "Mabilisang simula:",
  "roles.seeEverything": "Nakikita ang lahat",
  "roles.doEverything": "Nagagawa ang lahat",
  "roles.clearAll": "Alisin lahat",
  "roles.count": "{selected} sa {total} pahintulot",
  "roles.kindOfRecord": "Uri ng tala",
  "roles.noPermissions": "Walang tinukoy na pahintulot.",
  "roles.createRole": "Likhain ang tungkulin",
  "roles.saveRole": "I-save ang tungkulin",
  "roles.forEvery": "{verb} para sa bawat uri ng tala",
  "roles.view": "Tingnan",
  "roles.viewHint": "makakabukas at makakabasa",
  "roles.add": "Magdagdag",
  "roles.addHint": "makakalikha ng bago",
  "roles.change": "I-edit",
  "roles.changeHint": "makakabago ng umiiral",
  "roles.remove": "Tanggalin",
  "roles.removeHint": "makakaalis",
  "roles.labelGroup": "Tungkulin",
  "roles.labelUser": "User (empleyado)",
  "policy.text":
    "Gaano katagal maaaring manatiling tigil ang session, at gaano kadalas dapat palitan ang password",
  "policy.globalText":
    "Ito ang para sa bawat tungkulin at user maliban kung babaguhin sa ibaba",
  "policy.roleText": "Ito ang para sa bawat {role} maliban kung babaguhin para sa tao",
  "policy.global": "Pangkalahatang {title}",
  "policy.roleBased": "{title} Batay sa Tungkulin",
  "policy.individual": "Indibidwal na {title}",
  "policy.system": "Sistema",
  "policy.addEmployee": "Magdagdag ng Empleyado",
  "policy.employee": "Empleyado",
  "policy.chooseEmployee": "Pumili ng empleyado",
  "policy.nobody": "Walang may sariling halaga; lahat ay nagmamana.",
  "policy.leaveTitle": "Aalis nang hindi sine-save?",
  "policy.leaveUnsaved":
    "May mga hindi pa naka-save na pagbabago. Aalis nang hindi ito sine-save?",
  "policy.saveChanges": "I-save ang mga pagbabago",
  "policy.discard": "Itapon",
  "policy.unsavedCount": "{n} ang hindi pa naka-save",
  "policy.default": "Default",
  "policy.orgName": "Pangalan ng organisasyon",
  "policy.orgHint": "Wala pang organisasyon; lilikhain ito.",
  "policy.orgCreated": "Nalikha ang organisasyon.",
  "policy.orgCreateFailed": "Hindi malikha ang organisasyon.",
  "reasons.intro":
    "I-configure ang mga pagpipiliang dahilan para sa iba't ibang sitwasyon sa buong application",
  "reasons.cardReplacement": "Pagpapalit ng Card",
  "reasons.blacklist": "Blacklist",
  "reasons.cancellation": "Pagkansela",
  "reasons.cardReplacementHeading": "Mga Dahilan ng Pagpapalit ng Card",
  "reasons.blacklistHeading": "Mga Dahilan ng Blacklist",
  "reasons.cancellationHeading": "Mga Dahilan ng Pagkansela",
  "reasons.amendmentReason": "Dahilan ng Amendment",
  "reasons.amendmentType": "Uri ng Amendment",
  "reasons.reason": "Dahilan",
  "reasons.addReason": "Magdagdag ng dahilan",
  "reasons.addNew": "Magdagdag",
  "reasons.endorsementHeading": "Dahilan ng Endorsement",
  "reasons.notesReason": "Dahilan ng Tala",
  "reasons.notesTitle": "Pamagat ng Tala",
  "reasons.endorsement": "Pag-endorso",
  "reasons.endorsedBy": "Inendorso Ni",
  "reasons.endorsedByTitle": "Tala ng Complimentary Inendorso Ni",
  "reasons.endorsedByList": "Listahan ng Nag-endorso",
  "reasons.name": "Pangalan",
  "reasons.approvedBy": "Inaprubahan Ni",
  "reasons.approvedByTitle": "Tala ng Complimentary Inaprubahan Ni",
  "reasons.approvedByList": "Listahan ng Nag-apruba",
  "reasons.editTitle": "I-edit ang {title}",
  "reasons.createTitle": "Likhain ang {title}",
  "reasons.typeSomething": "Mag-type dito",
  "reasons.confirmDelete": 'Tanggalin ang "{name}"?',
  "promo.showExpired": "Ipakita ang mga paso na",
  "promo.noDescription": "Walang paglalarawan",
  "promo.title": "Mga Promo Code",
  "promo.intro": "Pamahalaan ang mga promo code para sa mga diskwento at alok sa miyembro.",
  "promo.add": "Magdagdag ng promo code",
  "promo.validUntil": "Hanggang",
  "promo.deleted": "Natanggal ang promo code.",
  "promo.deleteFailed": "Hindi matanggal ang promo code.",
  "promo.none": "Wala pang promo code.",
  "promo.confirmDelete": "Tanggalin ang promo code {code}?",
  "promo.edit": "I-edit ang Promo Code",
  "promo.create": "Likhain ang Promo Code",
  "promo.code": "Kodigo ng Promo",
  "promo.description": "Paglalarawan",
  "promo.enter": "Ilagay",
  "promo.updated": "Na-update ang promo code.",
  "promo.created": "Nalikha ang promo code.",
  "promo.saveFailed": "Hindi ma-save ang promo code.",

  "rp.search": "Maghanap...",
  "load.loading": "Ikinakarga ang {subject}…",
  "load.searching": "Hinahanap ang {subject}…",
  "load.filtering": "Isinasagawa ang mga filter…",
  "load.sorting": "Isinasaayos ang {subject}…",
  "load.paging": "Kinukuha ang susunod na pahina…",
  "load.saving": "Sine-save…",
  "load.deleting": "Inaalis…",
  "load.exporting": "Inihahanda ang export…",
  "load.stillWorking": "Ginagawa pa — maaaring tumagal ang malaking paghahanap.",
  "rp.searchHistory": "Kasaysayan ng paghahanap",
  "rp.suggestions": "Mga mungkahi",
  "rp.removeSearch": "Alisin ang {term}",
  "rp.timeRange": "Saklaw ng Oras",
  "rp.searchHistoryKept":
    "Inaalis ang mga paghahanap na hindi mo inulit sa loob ng isang buwan.",
  "rp.lastHour": "Nakaraang oras",
  "rp.last24h": "Nakaraang 24 oras",
  "rp.last7d": "Nakaraang 7 araw",
  "rp.last4w": "Nakaraang 4 na linggo",
  "rp.allTime": "Lahat ng Oras",
  "rp.filters": "Mga Filter",
  "rp.clearFilter": "Alisin ang filter",
  "rp.clearFilters": "Alisin ang {count} filter",
  "rp.clearFiltersBtn": "Alisin ang mga filter",
  "rp.export": "I-export",
  "rp.moreActions": "Iba pang aksyon",
  "rp.loadFailedTitle": "Hindi ma-load ang listahan",
  "rp.loadFailedBody":
    "Walang naibalik na sagot ang kahilingan. Suriin ang koneksyon at subukan muli — walang binago.",
  "rp.retry": "Subukan muli",
  "rp.confirmDeleteTitle": "Burahin ang {singular} na ito?",
  "rp.refresh": "I-refresh",
  "rp.columns": "Mga Column",
  "rp.updatedAt": "Huling Binago",
  "rp.updatedBy": "Binago Ni",
  "rp.customizeColumns": "I-customize ang mga Column",
  "rp.add": "Magdagdag ng {title}",
  "rp.back": "Bumalik",
  "rp.matching": "tumutugma sa “{search}”",
  "rp.filterOn": "{count} filter ang nakabukas",
  "rp.filtersOn": "{count} filter ang nakabukas",
  "rp.noMatch": "Walang tumutugmang {plural}",
  "rp.tryAnother": "Subukan ang ibang baybay, o luwagan ang mga filter.",
  "rp.noneYet": "Wala pang {plural}",
  "rp.entriesAppear": "Lilitaw dito ang mga tala habang may nangyayari.",
  "rp.firstAdd": "Lilitaw dito ang unang {singular} na idadagdag mo.",
  "rp.showing": "Ipinapakita",
  "rp.pageN": "Pahina {n}",
  "mem.csvRowsOnPage": "Mga row {from}–{to} ng {total}",
  "mem.csvLine": "Linya",
  "mem.submitThisPage": "Isumite ang pahinang ito ({count})",
  "mem.submitAll": "Isumite lahat ({count})",
  "mem.csvMissingColumns":
    "Kulang ang file na ito ng {columns}. I-download ang template at gamitin ang header nito.",
  "mem.csvExpectedHeader": "Dapat na header:",
  "mem.csvProgress": "{done} sa {total} na hilera",
  "mem.csvTally": "{succeeded} ang naipasa, {failed} ang hindi",
  "mem.csvStop": "Itigil",
  "mem.csvSending": "Ipinapadala ang mga row. Iwanang bukas ito hanggang matapos.",
  "mem.csvRefused":
    "{count} row ang tinanggihan — nasa ibaba ang mga ito, may dahilan sa tabi ng bawat isa.",
  "mem.csvOutcome": "Ang nangyari sa bawat row",
  "mem.csvPending": "Nakabinbin",
  "mem.csvSucceeded": "Matagumpay",
  "mem.csvNoneSucceeded": "Wala pang row na pumasok.",
  "mem.csvNonePending": "Wala nang natitira — pumasok ang bawat row.",
  "mem.csvResult": "Bakit tinanggihan",
  "mem.csvStopped":
    "Itinigil. Nananatili ang naisulat na — ipadala muli ang parehong file upang tapusin ang natitira.",
  "mem.csvUnchanged": "{count} ang mayroon na ng hinihingi ng file.",
  "mem.csvFixFailed": "Itama ang {count} na hilerang nabigo",
  "mem.csvFileHeader": "Header ng file na ito:",
  "mem.csvReady": "{rows} row ang nabasa mula sa {name}.",
  "mem.reviewAndEdit": "Suriin at i-edit",
  "rp.rowActions": "Mga aksyon sa row",
  "rp.view": "Tingnan",
  "rp.stampCreated": "Nalikha",
  "rp.stampUpdated": "Binago",
  "rp.id": "ID",
  "rp.customizeColumn": "I-customize ang Column",
  "rp.showHide": "Ipakita/Itago ang mga column sa talahanayan.",
  "rp.ok": "OK",
  "rp.clear": "Alisin",
  "rp.applyFilter": "Ilapat ang Filter",
  "rp.addSingular": "Magdagdag ng {singular}",
  "rp.editSingular": "I-edit ang {singular}",
  "rp.save": "I-save",
  "rp.deleted": "Natanggal ang {singular}.",
  "rp.deleteFailed": "Hindi matanggal ang {singular}.",
  "rp.confirmDelete": "Tanggalin ang {singular} na ito? Hindi na ito maibabalik.",
  "rp.created": "Nalikha ang {singular}.",
  "rp.updated": "Na-update ang {singular}.",
  "rp.saveFailed": "Hindi ma-save ang {singular}.",
  "rp.chooseFirst": "Pumili muna ng {parent}",
  "rp.select": "Pumili ng {label}",
  "rp.selectDots": "Pumili…",
  "rp.yes": "Oo",
  "rp.no": "Hindi",
  "rp.changeImage": "Palitan ang larawan",
  "rp.dropImage": "I-drag at i-drop o i-click para mag-upload (jpg, png)",
  "rp.fileAttached": "May nakalakip na file ({kb} KB) — i-click para palitan",
  "rp.dropFile": "I-drag at i-drop o i-click para mag-upload ng file",
  "geo.provinces": "Mga Probinsya",
  "geo.province": "Probinsya",
  "geo.cities": "Mga Lungsod",
  "geo.city": "Lungsod",
  "geo.barangays": "Mga Barangay",
  "geo.barangay": "Barangay",
  "geo.name": "Pangalan",
  "geo.code": "Code",
  "geo.population": "Populasyon",
  "geo.latitude": "Latitud",
  "geo.longitude": "Longhitud",
  "geo.createdBy": "Nilikha Ni",
  "geo.createdAt": "Nilikha Noong",
  "geo.createdByFilter": "Nilikha ni",
  "geo.createdFrom": "Nilikha mula",
  "geo.createdTo": "Nilikha hanggang",

  "theme.light": "Maliwanag na tema",
  "theme.dark": "Madilim na tema",
  "theme.lightValue": "Maliwanag",
  "theme.darkValue": "Madilim",
  "language.label": "Wika",

  "page.mapsText": "Saklaw at populasyon sa mapa.",
  "page.nothingYet": "Wala pang magagawa dito.",
  "page.backToDashboard": "Bumalik sa pangunahing panel",

  "nm.singular": "Hindi Miyembro",
  "nm.date": "Petsa",
  "nm.name": "Pangalan",
  "nm.birthDate": "Petsa ng Kapanganakan",
  "nm.promoCode": "Promo Code",
  "nm.promoCodeFilter": "Promo code",
  "nm.club": "Club",
  "nm.actor": "Nagtala",
  "nm.visitedFrom": "Bumisita mula",
  "nm.visitedTo": "Bumisita hanggang",
  "nm.firstName": "Pangalan",
  "nm.middleName": "Gitnang Pangalan",
  "nm.lastName": "Apelyido",

  "log.entry": "Tala",
  "log.memberNumberSearch": "Numero ng Miyembro",
  "log.memberAccountNumber": "Account Number ng Miyembro",
  "log.modification": "Uri ng Pagbabago",
  "log.from": "Mula",
  "log.to": "Naging",
  "log.modifiedAt": "Binago Noong",
  "log.modifiedBy": "Binago Ni",
  "log.clubLocation": "Lokasyon ng Club",
  "log.fromDate": "Mula sa petsa",
  "log.toDate": "Hanggang sa petsa",
  "log.employeeSearch": "Employee ID / Pangalan",
  "log.employee": "Empleyado",
  "log.reason": "Dahilan",
  "log.status": "Katayuan",
  "log.actor": "Gumawa",
  "log.handledBy": "Hinawakan ni",
  "log.employeeNo": "Employee No.",
  "log.name": "Pangalan",
  "log.date": "Petsa",

  "ypc.category": "Kategorya",

  "yp.title": "Yellow Pages",
  "yp.description": "Bawat negosyo at opisinang nakalista sa buong Pilipinas.",
  "yp.categories": "Mga Kategorya",
  "yp.addListing": "Magdagdag ng Listing",
  "yp.editListing": "I-edit ang Listing",
  "yp.listing": "Listing",
  "yp.searchListings": "Maghanap ng listing",
  "yp.listings": "mga listing",
  "yp.anyCity": "Kahit anong lungsod",
  "yp.anyCategory": "Kahit anong kategorya",
  "yp.myTags": "Aking mga tag",
  "yp.tagged": "Na-tag bilang paborito.",
  "yp.tagRemoved": "Naalis ang tag.",
  "yp.tagFailed": "Hindi ma-update ang tag.",
  "yp.tagFavourite": "I-tag bilang paborito",
  "yp.removeTag": "Alisin ang tag",
  "yp.listingDeleted": "Natanggal ang listing.",
  "yp.deleteFailed": "Hindi matanggal ang listing.",
  "yp.deleteListing": "Tanggalin ang listing",
  "yp.confirmDelete": "Tanggalin ang {name}? Hindi na ito maibabalik.",
  "yp.thisListing": "listing na ito",
  "yp.noListings": "Walang nahanap na listing",
  "yp.tryDifferent": "Subukan ang ibang paghahanap, o alisin ang mga filter.",
  "yp.listingsAppear":
    "Lilitaw dito ang mga listing kapag may naidagdag nang negosyo sa direktoryo.",
  "yp.countOf": "{shown} sa {total} listing",
  "yp.setOutFrom": "Magsimula mula sa",
  "yp.homeClub": "Pangunahing club",
  "yp.temporaryClub": "Pansamantalang club",
  "yp.directionsFrom": "Direksyon mula sa {club}",
  "yp.location": "Lokasyon",
  "yp.hideMap": "Itago ang mapa",
  "yp.showMap": "Ipakita ang mapa",
  "yp.findingWay": "Hinahanap ang daan…",
  "yp.routeFailed": "Hindi makuha ang ruta sa ngayon; ipinapakita ng mapa ang dalawang dulo.",
  "yp.byCar": "{distance} · mga {time} sakay ng kotse",
  "yp.straightLine": "{km} km sa tuwid na linya",
  "yp.hideSteps": "Itago ang {n} hakbang",
  "yp.showSteps": "Ipakita ang {n} hakbang",
  "yp.turnLeft": "Kumaliwa",
  "yp.turnRight": "Kumanan",
  "yp.bearLeft": "Bahagyang kumaliwa",
  "yp.bearRight": "Bahagyang kumanan",
  "yp.sharpLeft": "Matalim na kaliwa",
  "yp.sharpRight": "Matalim na kanan",
  "yp.straight": "Dumiretso",
  "yp.uturn": "Mag-U-turn",
  "yp.arrive": "Dumating sa destinasyon",
  "yp.headOutOn": "Magsimula sa {road}",
  "yp.headOut": "Magsimula",
  "yp.roundabout": "Dumaan sa rotonda",
  "yp.merge": "Sumanib",
  "yp.keep": "Manatili sa {direction}",
  "yp.ahead": "unahan",
  "yp.continue": "Magpatuloy",
  "yp.onto": "papuntang {road}",
  "yp.category": "Kategorya ng Yellow Page",
  "yp.selectCategory": "Pumili ng kategorya",
  "yp.addCategory": "Magdagdag ng kategorya",
  "yp.locationHint": "Ang lungsod o munisipalidad.",
  "yp.selectCity": "Pumili ng lungsod",
  "yp.fullAddress": "Buong Adres",
  "yp.phone": "Telepono",
  "yp.photos": "Mga Larawan",
  "yp.dropPhotos": "I-drag at i-drop para mag-upload ng larawan (jpg, png)",
  "yp.photosOf": "{n} sa {max}",
  "yp.removePhoto": "Alisin ang larawan",
  "yp.newCategory": "Bagong kategorya",
  "yp.newCategoryHint": "Agad itong magagamit ng bawat listing.",
  "yp.add": "Idagdag",
  "yp.categoryAdded": "Naidagdag ang kategoryang “{name}”.",
  "yp.categoryFailed": "Hindi maidagdag ang kategorya.",
  "yp.imageFailed": "Hindi mabasa ang larawang iyon.",
  "yp.listingUpdated": "Na-update ang listing.",
  "yp.listingAdded": "Naidagdag ang listing.",
  "yp.saveFailed": "Hindi ma-save ang listing.",

  "emp.employeeNumber": "Numero ng Empleyado",
  "emp.search": "Anumang bahagi ng pangalan, o buong Employee ID o email",
  "emp.name": "Pangalan",
  "emp.firstName": "Unang Pangalan",
  "emp.middleName": "Gitnang Pangalan",
  "emp.lastName": "Apelyido",
  "emp.emailAddress": "Email Address",
  "emp.club": "Club",
  "emp.temporaryClub": "Pansamantalang Club",
  "emp.temporaryClubFilter": "Pansamantalang club",
  "emp.role": "Tungkulin",
  "emp.manager": "Manager",
  "emp.status": "Katayuan",
  "emp.statusActive": "Aktibo",
  "emp.statusLocked": "Naka-lock ang Account",
  "emp.statusExpired": "Paso na ang Password",
  "emp.statusInactive": "Hindi Aktibo",
  "emp.statusDeleted": "Tinanggal",
  "emp.passwordRequest": "Kahilingan sa password",
  "emp.sectionPicture": "Larawan",
  "emp.sectionIdentity": "Pagkakakilanlan",
  "emp.sectionAccess": "Access",
  "emp.sectionClub": "Club",
  "emp.sectionPassword": "Password",
  "emp.isSuperuser": "Superuser",
  "emp.forcePasswordChange": "Pilitin ang Pagpapalit ng Password",
  "emp.isPasswordRequest": "May Kahilingan sa Password",
  "emp.requestReason": "Dahilan ng Kahilingan",
  "emp.requestedAt": "Hiniling Noong",
  "emp.lastPasswordChange": "Huling Pagpapalit ng Password",
  "emp.lastLogin": "Huling Pag-sign in",
  "emp.passwordRequestAction": "Kahilingan sa password…",
  "emp.stopRequiring": "Huwag nang hingin ang bagong password",
  "emp.requireNew": "Hingin ang bagong password",
  "emp.noLongerRequired": "Hindi na kailangan.",
  "emp.requiredNext": "Kakailanganin sa susunod na pag-sign in.",
  "emp.deactivate": "I-deactivate",
  "emp.activate": "I-activate",
  "emp.deactivated": "Na-deactivate ang empleyado.",
  "emp.activated": "Na-activate ang empleyado.",
  "emp.updateFailed": "Hindi ma-update ang empleyado.",
  "emp.reason": "Dahilan",
  "emp.requested": "Hiniling",
  "emp.reject": "Tanggihan",
  "emp.approve": "Aprubahan",
  "emp.sentToManager": "Naipadala ang password sa manager.",
  "emp.requestRejected": "Tinanggihan ang kahilingan.",
  "emp.requestFailed": "Hindi ma-update ang kahilingan.",
  "emp.create": "Gumawa ng Empleyado",
  "emp.edit": "I-edit ang Empleyado",
  "emp.lookingUp": "Hinahanap sa SNR…",
  "emp.foundRole": "Nahanap sa SNR. Titulo sa HR: {position} → {role}.",
  "emp.foundNoRole":
    "Nahanap sa SNR. Walang tungkuling tumutugma sa titulong “{position}”; pumili ng isa.",
  "emp.snrFailed": "Hindi mahanap ang empleyado sa SNR.",
  "emp.fillByHand": "Punan ang mga detalye nang mano-mano.",
  "emp.enterEmployeeNumber": "Ilagay ang Numero ng Empleyado",
  "emp.enterName": "Ilagay ang Pangalan",
  "emp.selectRole": "Pumili ng Tungkulin",
  "emp.selectClub": "Pumili ng Club",
  "emp.temporaryPassword": "Pansamantalang Password",
  "emp.generate": "Bumuo",
  "emp.passwordHint": "Hihilingin sa kanilang pumili ng sarili sa unang pag-sign in.",
  "emp.clickToGenerate": "I-click para bumuo ng password",
  "emp.selectManager": "Pumili ng Manager",
  "emp.addTemporaryClub": "Magdagdag ng Pansamantalang Club",
  "emp.whyPosting": "Dahilan ng pagtalaga",
  "emp.startDate": "Petsa ng Simula",
  "emp.endDate": "Petsa ng Pagtatapos",
  "emp.created": "Nalikha ang empleyado.",
  "emp.updated": "Na-update ang empleyado.",
  "emp.saveFailed": "Hindi ma-save ang empleyado.",
  "emp.pictureUpdated": "Na-update ang larawan.",
  "emp.pictureRemoved": "Naalis ang larawan.",
  "emp.pictureFailed": "Hindi ma-update ang larawan.",

  "an.description":
    "Bilang ng miyembro bawat club, direkta mula sa talahanayan ng mga miyembro.",
  "an.countedAt": "Nabilang {when}.",
  "an.notCounted": "Hindi pa nabibilang; bibilangin ng worker sa susunod nitong takbo.",
  "an.upToDate": "Walang miyembrong nagbago mula noon.",
  "an.recount": "Bilangin muli",
  "an.recounting": "Binibilang muli…",
  "an.recounted": "Napapanahon na ang mga bilang.",
  "an.totalMembers": "Kabuuang miyembro",
  "an.primary": "Pangunahin",
  "an.supplementary": "Suplementaryo",
  "an.clubs": "Mga Club",
  "an.searchClub": "Maghanap ng pangalan ng club",
  "an.business": "Negosyo",
  "an.goldMember": "Gold Member",
  "an.affiliates": "Mga Kaanib",
  "an.complimentary": "Komplimentaryo",
  "an.employee": "Empleyado",
  "an.total": "Kabuuan",
  "an.clubName": "Pangalan ng Club",
  "an.noClubs": "Walang tumutugmang club.",
  "an.allClubs": "Lahat ng club",
  "an.membersCount": "{n} miyembro",
  "an.accountType": "Uri ng Account",
  "an.sortBy": "Ayusin ang bilang ayon sa",
  "an.both": "Pareho",

  "mem.singular": "Miyembro",
  "mem.mr": "G.",
  "mem.ms": "Bb.",
  "mem.mrs": "Gng.",
  "mem.dr": "Dr.",
  "mem.atty": "Atty.",
  "mem.engr": "Engr.",
  "mem.female": "Babae",
  "mem.male": "Lalaki",
  "mem.other": "Iba",
  "mem.undisclosed": "Mas gustong hindi sabihin",
  "mem.walkIn": "Walk-in",
  "mem.canvassing": "Pangangalap",
  "mem.benefits": "Benepisyo",
  "mem.business": "Negosyo",
  "mem.gold": "Gold",
  "mem.unioil": "UniOil",
  "mem.qsr": "QSR",
  "mem.affiliates": "Mga Kaanib",
  "mem.complimentary": "Komplimentaryo",
  "mem.employee": "Empleyado",
  "mem.member": "Miyembro",
  "mem.primary": "Pangunahin",
  "mem.supplementary": "Suplementaryo",
  "mem.active": "Aktibo",
  "mem.expired": "Paso na",
  "mem.cancelled": "Kanselado",
  "mem.blacklisted": "Naka-blacklist",
  "mem.approved": "Aprubado",
  "mem.declined": "Tinanggihan",
  "mem.pending": "Nakabinbin",
  "mem.spouse": "Asawa",
  "mem.child": "Anak",
  "mem.parent": "Magulang",
  "mem.sibling": "Kapatid",
  "mem.sectionIdentity": "Pagkakakilanlan",
  "mem.sectionAccount": "Account",
  "mem.sectionCard": "Card",
  "mem.sectionClubsAddress": "Mga Club at Adres",
  "mem.sectionContactConsent": "Contact at Pahintulot",
  "mem.sectionBusiness": "Negosyo",
  "mem.sectionImages": "Mga Larawan",
  "mem.prefix": "Prefix",
  "mem.suffix": "Suffix",
  "mem.firstName": "Pangalan",
  "mem.middleName": "Gitnang Pangalan",
  "mem.lastName": "Apelyido",
  "mem.name": "Pangalan",
  "mem.hasLastName": "May Apelyido",
  "mem.gender": "Kasarian",
  "mem.dateOfBirth": "Petsa ng Kapanganakan",
  "mem.accNo": "Acc. No.",
  "mem.snrAccountNumber": "S&R Account Number",
  "mem.accountType": "Uri ng Account",
  "mem.accountStatus": "Katayuan ng Account",
  "mem.membershipType": "Uri ng Pagkamiyembro",
  "mem.membershipTier": "Antas ng Pagkamiyembro",
  "mem.acquisitionType": "Uri ng Pagkuha",
  "mem.memberSince": "Miyembro Mula",
  "mem.amendment": "Amendment",
  "mem.parentAccount": "Pangunahing Account",
  "mem.supplementaryAccount": "Suplementaryong Account",
  "mem.hasSupplementaryCreated": "May Nalikhang Suplementaryo",
  "mem.familyMember": "Kapamilya",
  "mem.employeeNumber": "Numero ng Empleyado",
  "mem.employeeRelation": "Kaugnayan sa empleyado",
  "mem.volumeShopper": "Volume Shopper",
  "mem.coBrandTagging": "CO-Brand Tagging",
  "mem.cardName": "Pangalan sa Card",
  "mem.cardNumber": "Numero ng Card",
  "mem.temporaryCardNumber": "Pansamantalang Numero ng Card",
  "mem.issuedDate": "Petsa ng Pag-isyu",
  "mem.validUntil": "Balido Hanggang",
  "mem.noExpiry": "Walang Pagkapaso",
  "mem.isPrintReady": "Handa nang I-print",
  "mem.temporarilyActive": "Pansamantalang Aktibo",
  "mem.extendedTime": "Dagdag na Oras (Minuto)",
  "mem.signupClub": "Club ng Pag-sign up",
  "mem.activeClub": "Aktibong Club",
  "mem.renewClub": "Club ng Pag-renew",
  "mem.city": "Lungsod",
  "mem.barangay": "Barangay",
  "mem.addressStreet": "Adres/Kalye",
  "mem.unitNumber": "Numero ng Unit",
  "mem.buildingName": "Pangalan ng Gusali",
  "mem.mobileNumber": "Numero ng Mobile",
  "mem.email": "Email",
  "mem.phoneConsent": "Pahintulot sa Telepono",
  "mem.emailConsent": "Pahintulot sa Email",
  "mem.onlyImportantUpdates": "Mahahalagang Update Lamang",
  "mem.agreeTerm": "Sumasang-ayon sa Tuntunin",
  "mem.agreePersonalInfo": "Sumasang-ayon sa Personal na Impormasyon",
  "mem.businessName": "Pangalan ng Negosyo",
  "mem.businessType": "Uri ng Negosyo",
  "mem.businessCompanyName": "Pangalan ng Kumpanya ng Negosyo",
  "mem.companyName": "Pangalan ng Kumpanya",
  "mem.position": "Posisyon",
  "mem.idCard": "ID card",
  "mem.profileImage": "Larawan sa Profile",
  "mem.advancedFilter": "Advanced na filter",
  "mem.addMember": "Magdagdag ng Miyembro",
  "mem.fullName": "Buong Pangalan",
  "mem.accountNumber": "Account Number",
  "mem.signUpClub": "Club ng Pag-sign up",
  "mem.isEmailConsent": "May Pahintulot sa Email",
  "mem.isPhoneConsent": "May Pahintulot sa Telepono",
  "mem.withEmail": "May Email",
  "mem.withoutEmail": "Walang Email",
  "mem.withMobile": "May Mobile Number",
  "mem.withoutMobile": "Walang Mobile Number",
  "mem.withTag": "May Tag",
  "mem.withoutTag": "Walang Tag",
  "mem.createdBy": "Nilikha Ni",
  "mem.createdByFilter": "Nilikha ni",
  "mem.modifiedBy": "Binago Ni",
  "mem.modifiedAt": "Binago Noong",
  "mem.memberN": "Miyembro {id}",
  "mem.batch": "Batch",
  "mem.savedFilters": "Mga naka-save na filter",
  "mem.filtersCount": "{count} filter",
  "mem.applied": "Nakalapat",
  "mem.viewAllSaved": "Tingnan lahat ({count})",
  "mem.savedFiltersHint": "Ilapat ang isang set, o i-edit at tanggalin ang mga itinatago mo.",
  "mem.editFilter": "I-edit ang “{name}”",
  "mem.editName": "I-edit ang {name}",
  "mem.saveChanges": "I-save ang mga pagbabago",
  "mem.noSavedFilters": "Wala pang naka-save na filter",
  "mem.noSavedFiltersBody":
    "Magtakda ng mga kondisyon sa Advanced na filter at piliin ang I-save ang set.",
  "mem.batchUpload": "Batch Upload",
  "mem.batchUpdate": "Batch Update",
  "mem.cancellation": "Pagkansela",
  "mem.hintYesNo": "yes o no",
  "mem.hintCancellation":
    "Cancellation ang nakalagay sa amendment_cancellation; ang dahilan ay isa sa mga naka-configure",
  "mem.hintDate": "petsang gaya ng 9/1/2026",
  "mem.hintEmployee": "ang adres, lungsod, barangay, at Employee ID Number",
  "mem.hintAffiliates": "ang Pangalan ng Kumpanya",
  "mem.hintComplimentary": "ang account number ng S&R Family Owner",
  "mem.templateHeaders": "ang mga header ng template",
  "mem.hintUpload":
    "pangalan, petsa ng kapanganakan, kasarian, contact, {extra}, saka ang mga pahintulot bilang yes o no",
  "mem.csvDescription":
    "Isang CSV na may mga column na {columns} ({hint}). Panatilihing text ang mga column ng numero, kung hindi ay bibilugin ito ng spreadsheet. Hanggang 10,000 row.",
  "mem.processing": "Pinoproseso",
  "mem.downloadTemplate": "I-download ang sample template",
  "mem.csvFile": "CSV file",
  "mem.processFailed": "Hindi maproseso ang file.",
  "mem.membersUpdated": "{count} miyembro ang na-update.",
  "mem.membersCreated": "{count} miyembro ang nalikha.",
  "mem.updatedCount": "{count} ang na-update.",
  "mem.createdCount": "{count} ang nalikha.",
  "mem.rowsSkipped": "{count} row ang nilaktawan.",
  "mem.rowN": "Row {n}: ",
  "mem.nothingApplied": "Wala pang inilalapat.",
  "mem.conditionsSet": "{count} kondisyon ang nakatakda.",
  "mem.saveSet": "I-save ang set",
  "mem.reset": "I-reset",
  "mem.apply": "Ilapat",
  "mem.mySavedFilters": "Aking mga naka-save na filter",
  "mem.applyThisFilter": "Ilapat ang filter na ito",
  "mem.rename": "Palitan ang pangalan",
  "mem.renameName": "Palitan ang pangalan ng {name}",
  "mem.deleteName": "Tanggalin ang {name}",
  "mem.someone": "may gumawa",
  "mem.membershipSince": "Miyembro mula",
  "mem.from": "Mula",
  "mem.to": "Hanggang",
  "mem.groupMemberStatus": "Katayuan ng Miyembro",
  "mem.groupCardInformation": "Impormasyon ng Card",
  "mem.groupAccountDetails": "Mga Detalye ng Account",
  "mem.groupClubInformation": "Impormasyon ng Club",
  "mem.groupAddress": "Tirahan",
  "mem.searchPlaceholder": "Pangalan, account, S&R o card number…",
  "mem.exportTitle": "I-export ang mga miyembro",
  "mem.exportBody": "Hawak ng screen ang {onScreen} sa {total} na tugmang miyembro.",
  "mem.exportThisPage": "Ang page na ito ({count})",
  "mem.exportEverything": "Lahat ({count})",
  "mem.exportEverythingUncounted": "Lahat ng tugmang miyembro",
  "mem.exportNote":
    "Isinusulat ng server ang mga row habang binabasa ang mga ito, kaya dumarating na ang file bago pa ito matapos. Panatilihing bukas ito hanggang matapos.",
  "mem.exportFailed": "Hindi naihanda ang export.",
  "mem.exportRows": "Mga miyembro",
  "mem.exportColumns": "Mga column",
  "mem.exportColumnCount": "{count} sa {total} na column sa file.",
  "mem.exportSelectAll": "Piliin lahat",
  "mem.exportClearAll": "Alisin lahat",
  "mem.exportFilters": "Mga filter",
  "mem.exportFilterCount": "{count} ang nakalapat",
  "mem.exportNoFilters": "Walang nakalapat",
  "mem.exportNeedsFilter":
    "Magdagdag ng kahit isang filter bago i-export ang lahat. Kung walang filter, bawat miyembro sa talaan ito — isang file na hindi kayang buksan at ilang minutong pagsusulat ng server.",
  "mem.exportRunningTitle": "Ine-export ang mga miyembro",
  "mem.exportRunningBody":
    "Isinusulat at dina-download ang file. Mananatili ang window na ito hanggang matapos.",
  "mem.exportElapsed": "{elapsed} ang lumipas",
  "mem.exportStop": "Kanselahin ang export",
  "mem.exportCancelled": "Kinansela ang export. Walang na-save.",
  "mem.exportKeepOpen": "Ihihinto ng pagsasara ng tab na ito ang download.",
  "mem.groupMembershipActivity": "Aktibidad ng Pagkamiyembro",
  "mem.groupMembershipProfile": "Profile ng Pagkamiyembro",
  "mem.groupPermission": "Pahintulot",
  "mem.groupSystemRelated": "Kaugnay ng Sistema",
  "mem.presetAll": "Lahat ng Miyembro",
  "mem.presetActive": "Lahat ng Aktibong Miyembro",
  "mem.presetActivePrimary": "Lahat ng Aktibong Pangunahin",
  "mem.presetActiveSupplementary": "Lahat ng Aktibong Suplementaryo",
  "mem.presetCancelled": "Lahat ng Kanselado",
  "mem.presetExpired": "Lahat ng Paso na",
  "mem.presetBlacklisted": "Lahat ng Naka-blacklist",
  "mem.presetPendingToPrint": "Naghihintay na I-print",
  "mem.cardIssuedDate": "Petsa ng Pag-isyu ng Card",
  "mem.cardExpiryDate": "Petsa ng Pagkapaso ng Card",
  "mem.volumeShopperTag": "Tag na Volume Shopper",
  "mem.unionBankTag": "Tag na Union Bank",
  "mem.cardReplacementHistory": "Kasaysayan ng Palit-Card",
  "mem.emailAddress": "Email Address",
  "mem.birthdate": "Kaarawan",
  "mem.mobilePhoneConsent": "Pahintulot sa Mobile Phone",
  "mem.createdDate": "Petsa ng Paglikha",
  "mem.lastModifiedDate": "Petsa ng Huling Pagbabago",
  "mem.sourceOfApplication": "Pinagmulan ng Aplikasyon",
  "mem.archived": "Naka-archive",
  "mem.addRow": "Magdagdag",
  "mem.selectField": "Pumili ng field",
  "mem.all": "Lahat",
  "mem.filterBy": "I-filter ayon sa",
  "mem.removeFilter": "Alisin ang filter",
  "mem.renameFilter": "Palitan ang pangalan ng filter",
  "mem.saveThisFilter": "I-save ang filter na ito",
  "mem.saveHint": "Lilitaw ito sa Aking mga naka-save na filter, handang ilapat muli.",
  "mem.filterSaved": "Na-save ang filter.",
  "mem.filterSaveFailed": "Hindi ma-save ang filter.",
  "mem.filterDeleted": "Natanggal ang filter.",
  "mem.filterDeleteFailed": "Hindi matanggal ang filter.",
  "mem.renameFailed": "Hindi mapalitan ang pangalan ng filter.",

  "md.upgrade": "Upgrade",
  "md.downgrade": "Downgrade",
  "md.cancellation": "Pagkansela",
  "md.blacklist": "Blacklist",
  "md.cardReplacement": "Pagpapalit ng Card",
  "md.photoUpdated": "Na-update ang larawan.",
  "md.photoRemoved": "Naalis ang larawan.",
  "md.photoFailed": "Hindi ma-update ang larawan.",
  "md.primaryUpdated": "Na-update ang pangunahing account.",
  "md.accountChangeFailed": "Hindi mapalitan ang account number.",
  "md.notFound": "Hindi nahanap ang miyembro",
  "md.notFoundBody": "Maaaring natanggal na ang talang ito, o luma na ang link.",
  "md.backToMembers": "Bumalik sa mga miyembro",
  "md.accountNumber": "Account Number:",
  "md.print": "I-print",
  "md.amendName.blacklist": "Blacklist",
  "md.amendName.cancellation": "Kanselasyon",
  "md.amendName.card-replacement": "Palitan ang card",
  "md.amendName.upgrade": "I-upgrade sa primary",
  "md.amendName.downgrade": "I-downgrade sa supplementary",
  "md.amendName.change-tier": "Palitan ang tier",
  "md.amendName.activate": "I-activate",
  "md.amendName.account-renew": "I-renew ang account",
  "md.amendName.change-primary-account": "Palitan ang primary account",
  "md.amend.blacklist":
    "Hinaharang ang account. Kasamang kakanselahin ang mga aktibong supplementary ng primary.",
  "md.amend.cancellation":
    "Tinatapos ang membership. Kasamang matatapos ang mga aktibong supplementary ng primary.",
  "md.amend.card-replacement":
    "Parehong account, bagong card: tataas ng isa ang huling dalawang digit ng card number at ipi-print.",
  "md.amend.upgrade":
    "Magiging sariling primary ang supplementary sa bagong card, aktibo nang isang taon.",
  "md.amend.downgrade":
    "Magiging supplementary ang miyembro sa ilalim ng pinangalanang primary.",
  "md.amend.change-tier":
    "Bagong card sa digit ng bagong tier at isang taong validity. Kailangang nakatala ang address.",
  "md.amend.activate": "Muling aktibo nang isang taon sa muling inisyung card.",
  "md.amend.account-renew":
    "Pinapayagang mamili ang expired na card nang sampung minuto; ang renewal sa till sa loob nito ang magpapanatili.",
  "md.amend.change-primary-account":
    "Inililipat ang supplementary sa ibang primary, sa card sa ilalim ng account na iyon.",
  "md.changeTier": "Palitan ang tier",
  "md.activate": "I-activate",
  "md.accountRenew": "I-renew ang account",
  "md.changePrimaryAccount": "Palitan ang primary account",
  "md.apply": "Ilapat",
  "md.amendmentApplied": "Nailapat ang amendment.",
  "md.amendmentFailed": "Hindi mailapat ang amendment.",
  "md.noAmendments": "Walang available na amendment",
  "md.selectReason": "Pumili ng dahilan",
  "md.others": "Iba pa",
  "md.specifyReason": "Tukuyin ang dahilan",
  "md.reasonRequired": "Kailangan ng dahilan.",
  "md.employeeNumberRequired": "Kailangan ng employee number ang employee membership.",
  "md.primaryRequired": "Kailangan ang account number ng primary.",
  "md.primaryAccountHint": "Account o card number ng primary",
  "md.printCard": "I-print ang card",
  "md.printBody": "Itatala ang pag-print, pagkatapos ay bubuksan ang print dialog.",
  "md.printReason": "Dahilan ng pag-print",
  "md.printReasonHint": "Bakit ipi-print ang card",
  "md.printNew": "Bagong card",
  "md.printRenew": "Renewal",
  "md.printLost": "Nawalang card",
  "md.printDamaged": "Sirang card",
  "md.printFailed": "Hindi maitala ang pag-print.",
  "md.membersConsent": "Pahintulot ng Miyembro",
  "md.onlyImportantUpdates": "Mahahalagang update lamang",
  "md.email": "Email",
  "md.phone": "Telepono",
  "md.na": "N/A",
  "md.cardInformation": "Impormasyon ng Card",
  "md.validUntil": "Balido hanggang",
  "md.basicInformation": "Pangunahing Impormasyon",
  "md.attachments": "Mga Attachment",
  "md.selectAmendment": "Pumili ng Amendment",
  "md.primaryAccountNo": "Pangunahing Account No.",
  "md.changeAccountNo": "Palitan ang account no.",
  "md.coBrandTagging": "Co-Brand Tagging",
  "md.coBrand": "Co-Brand",
  "md.changePrimaryTitle": "Palitan ang pangunahing account number",
  "md.changePrimaryBody": "Iuugnay nito ang suplementaryong tala sa ibang pangunahing account.",
  "md.primaryAccountNumber": "Pangunahing account number",
  "md.hide": "Itago",
  "md.show": "Ipakita",

  "hist.details": "Mga Detalye",
  "hist.cardHistory": "Kasaysayan ng Card",
  "hist.acquisitionDetails": "Mga Detalye ng Pagkuha",
  "hist.acquisitionDetail": "Detalye ng Pagkuha",
  "hist.printingHistory": "Kasaysayan ng Pag-print",
  "hist.print": "Print",
  "hist.transactionHistory": "Kasaysayan ng Transaksyon",
  "hist.cardTransaction": "Transaksyon ng Card",
  "hist.amendmentHistory": "Kasaysayan ng Amendment",
  "hist.amendmentDetail": "Detalye ng Amendment",
  "hist.primaryTable": "Talahanayan ng Pangunahin",
  "hist.supplementaryHistory": "Kasaysayan ng Suplementaryo",
  "hist.attachment": "Attachment",
  "hist.cardNoFrom": "Card No. Mula",
  "hist.cardNumberTo": "Card Number Patungo",
  "hist.cardNoTo": "Card No. Patungo",
  "hist.description": "Paglalarawan",
  "hist.amendmentDateTime": "Petsa / Oras ng Amendment",
  "hist.createdBy": "Nilikha Ni",
  "hist.noOfYears": "Bilang ng Taon",
  "hist.transactionNo": "Transaction No",
  "hist.paymentPurpose": "Layunin ng Bayad",
  "hist.amount": "Halaga",
  "hist.paymentType": "Uri ng Bayad",
  "hist.transactionDate": "Petsa ng Transaksyon",
  "hist.operatorNumber": "Numero ng Operator",
  "hist.printType": "Uri ng Print",
  "hist.printReason": "Dahilan ng Print",
  "hist.printStatus": "Katayuan ng Print",
  "hist.datePrinted": "Petsa ng Pag-print",
  "hist.cardNumber": "Numero ng Card",
  "hist.additionalReason": "Karagdagang Dahilan",
  "hist.cardReplacement": "Pagpapalit ng Card",
  "hist.newCard": "Bagong Card",
  "hist.damaged": "Sira",
  "hist.renew": "I-renew",
  "hist.new": "Bago",
  "hist.lost": "Nawala",
  "hist.others": "Iba pa",
  "hist.activate": "I-activate",
  "hist.upgrade": "Upgrade",
  "hist.downgrade": "Downgrade",
  "hist.pending": "Nakabinbin",
  "hist.printed": "Na-print",
  "hist.failed": "Nabigo",
  "hist.transactionNumber": "Numero ng Transaksyon",
  "hist.modeOfPayment": "Paraan ng Bayad",
  "hist.years": "Taon",
  "hist.renewClub": "Club ng Pag-renew",
  "hist.reverted": "Naibalik",
  "hist.registerNumber": "Numero ng Register",
  "hist.numberOfYears": "Bilang ng Taon",
  "hist.isReverted": "Naibalik ang Transaksyon",
  "hist.amendmentType": "Uri ng Amendment",
  "hist.reason": "Dahilan",
  "hist.oldAccountStatus": "Dating Katayuan ng Account",
  "hist.date": "Petsa",
  "hist.isPrinted": "Na-print na",
  "hist.name": "Pangalan",
  "hist.validTill": "Balido Hanggang",
  "hist.parentName": "Pangalan ng Pangunahin",
  "hist.parentCardNumber": "Numero ng Card ng Pangunahin",
  "hist.parentValidTill": "Balido Hanggang ng Pangunahin",
  "hist.title": "Pamagat",
  "hist.added": "Idinagdag",
  "hist.addedBy": "Idinagdag Ni",
  "hist.file": "File",
  "hist.deleted": "Natanggal ang {singular}.",
  "hist.deleteFailed": "Hindi matanggal ang {singular}.",
  "hist.download": "I-download",
  "hist.noneYet": "Wala pang {section}",
  "hist.noneBody": "Lilitaw dito ang anumang naitala sa {section} ng miyembrong ito.",
  "hist.previous": "Nakaraan",
  "hist.next": "Susunod",
  "hist.perPage": "{n} / pahina",
  "hist.confirmDeleteBody":
    "Aalisin nito ang tala mula sa rekord ng miyembro. Hindi na ito maibabalik.",

  "new.tagline": "Magdagdag ng bagong Miyembro sa pamilyang SNR",
  "new.whoToAdd": "Sino ang nais mong idagdag?",
  "new.member": "Miyembro",
  "new.affiliatesMember": "Kaanib na Miyembro",
  "new.employeeMember": "Miyembrong Empleyado",
  "new.complimentaryMember": "Komplimentaryong Miyembro",
  "new.enrolled": "Na-enroll si {name} bilang {number}.",
  "new.enrolFailed": "Hindi ma-enroll ang miyembro.",
  "new.contactRequired":
    "Magbigay ng kahit isang paraan ng pakikipag-ugnayan (Numero ng Mobile o Email).",
  "new.consentsRequired": "Kailangan ang dalawang pahintulot para magpatuloy.",
  "new.tooYoung": "Dapat 18 taong gulang pataas ang miyembro.",
  "new.mobileRequired":
    "Kailangan ng mobile number para maipadala ang mga update na pinahintulutan.",
  "new.emailRequired":
    "Kailangan ng email address para maipadala ang mga update na pinahintulutan.",
  "new.selectMembership": "Pumili ng Membership",
  "new.enterEmployeeNumber": "Ilagay ang Employee ID Number",
  "new.enterCompanyName": "Ilagay ang Pangalan ng Kompanya",
  "new.familyOwner": "S&R Family Owner",
  "new.inheritedFromPrimary":
    "Ang tier, membership at bisa ng card ay galing sa pangunahing account.",
  "new.idCard": "ID Card",
  "new.scanId": "I-scan ang ID",
  "new.photo": "Larawan",
  "new.capturePhoto": "Kumuha ng larawan",
  "new.autoFill": "Awtomatikong mapupunan ang mga ito kung i-scan muna ang ID card.",
  "new.selectAcquisition": "Pumili ng Pagkuha",
  "new.primaryAccountNumber": "Pangunahing Account Number",
  "new.primaryPlaceholder": "Ang account number ng pangunahing miyembro",
  "new.selectPrefix": "Pumili ng prefix",
  "new.selectSuffix": "Pumili ng suffix",
  "new.enterFirstName": "ILAGAY ANG PANGALAN",
  "new.enterMiddleName": "ILAGAY ANG GITNANG PANGALAN",
  "new.toggleNoLastName": "i-off kung walang apelyido",
  "new.enterLastName": "ILAGAY ANG APELYIDO",
  "new.selectGender": "Pumili ng kasarian",
  "new.enterEmail": "Ilagay ang email address",
  "new.contactNote":
    "Magbigay ng kahit isang paraan ng pakikipag-ugnayan (Numero ng Mobile o Email)",
  "new.enterUnitNumber": "Ilagay ang numero ng unit",
  "new.enterBuildingName": "Ilagay ang pangalan ng gusali",
  "new.enterAddress": "Ilagay ang adres/kalye",
  "new.selectCity": "Pumili ng lungsod",
  "new.selectBarangay": "Pumili ng barangay",
  "new.selectCityFirst": "Pumili muna ng lungsod",
  "new.receiveUpdates": "Tumanggap ng mga Update",
  "new.receiveBody":
    "Nais kong makatanggap ng mga update tungkol sa mga eksklusibong alok, promosyon at pinakabagong impormasyon tungkol sa mga bagong produkto at serbisyo mula sa S&R Membership Shopping sa pamamagitan ng sumusunod:",
  "new.privacyConsent": "Privacy at Pahintulot",
  "new.agreeTerms": "Nabasa ko at sumasang-ayon ako sa",
  "new.termsAndConditions": "mga tuntunin at kondisyon",
  "new.and": "at",
  "new.privacyPolicy": "patakaran sa privacy",
  "new.agreePersonal":
    "Sumasang-ayon at pumapayag ako na maaaring gamitin ng S&R Membership Shopping ang aking personal na impormasyon para sa",
  "new.statedReasons": "mga nakasaad na dahilan",
  "new.saving": "Sine-save",
  "new.clickToUpload": "I-click para Mag-upload",
  "new.orDrag": "o i-drag at i-drop",
  "new.pngOrJpg": "PNG o JPG",
  "new.primaryMember": "Pangunahing Miyembro",
  "new.primaryGateBody":
    "Ilagay ang account number ng pangunahing miyembro kung kanino ilalabas ang supplementary card na ito.",
  "new.lookingUp": "Hinahanap ang account\u2026",
  "new.primaryFound": "Nahanap ang account at karapat-dapat.",
  "new.primaryNotFound": "Hindi maaaring bigyan ng supplementary card ang account na ito.",
  "new.inheritsFromPrimary":
    "Ang card na ito ay magiging {tier} na membership, mula sa pangunahing account. {used} sa 2 supplementary card ang aktibo rito.",
  "new.verifyDetails": "Suriin ang mga detalye",
  "new.verifyBody":
    "Ilalabas ang card sa ilalim ng account na ito. Pakikumpirma bago magpatuloy.",
  "new.employeeGateBody":
    "Sinusuri ang employee number laban sa mga miyembrong nakatala bago buksan ang form.",
  "new.employeeTaken": "Nakatalaga na sa ibang miyembro ang employee number na iyan.",
  "new.duplicateMember": "May miyembro nang may ganitong pangalan at petsa ng kapanganakan.",
  "new.temporaryCardDigits": "Labing-isang digit",
  "new.employeeCheckFailed": "Hindi masuri ang employee number. Pakisubukan muli.",
  "new.checking": "Sinusuri",
  "new.approvedBy": "Inaprubahan Ni",
  "new.selectApprovedBy": "Piliin kung sino ang nag-apruba",
  "new.enterTemporaryCard": "Ilagay ang pansamantalang numero ng card",
  "new.walkInNeedsPictures": "Kailangan ng ID card at larawan para sa walk-in na pagpapatala.",
  "new.canvassingNeedsCard":
    "Kailangan ng pansamantalang numero ng card para sa canvassing na pagpapatala.",
  "common.back": "Bumalik",
  "common.confirm": "Kumpirmahin",

  "roles.model.member": "Miyembro",
  "roles.model.memberNote": "Tala ng miyembro",
  "roles.model.memberAttachment": "Attachment ng miyembro",
  "roles.model.memberLog": "Log ng miyembro",
  "roles.model.memberColumnSetting": "Setting ng column ng miyembro",
  "roles.model.memberSavedFilter": "Naka-save na filter ng miyembro",
  "roles.model.cardHistory": "Kasaysayan ng card",
  "roles.model.cardTransaction": "Transaksyon ng card",
  "roles.model.printHistory": "Kasaysayan ng pag-print",
  "roles.model.acquisitionDetail": "Detalye ng pagkuha",
  "roles.model.amendmentDetail": "Detalye ng amendment",
  "roles.model.supplementaryHistory": "Kasaysayan ng suplementaryo",
  "roles.model.nonMember": "Hindi miyembro",
  "roles.model.accessPolicySetting": "Setting ng patakaran sa access",
  "roles.model.employeeActivityLog": "Log ng aktibidad ng empleyado",
  "roles.model.organization": "Organisasyon",
  "roles.model.province": "Probinsya",
  "roles.model.city": "Lungsod",
  "roles.model.barangay": "Barangay",
  "roles.model.club": "Club",
  "roles.model.yellowPage": "Yellow page",
  "roles.model.yellowPageCategory": "Kategorya ng yellow page",
  "roles.model.promoCode": "Promo code",
  "roles.model.reason": "Dahilan",
  "roles.model.businessType": "Uri ng negosyo",

  "md.businessInformation": "Impormasyon ng Negosyo",
  "md.viewMore": "Tingnan pa",
  "md.viewLess": "Itago",
  "md.cardStatus": "Katayuan ng Card",
  "md.volumeShopperOn": "Volume Shopper: Naka-enable",
  "md.volumeShopperOff": "Volume Shopper: Naka-disable",
  "md.volumeShopperBody":
    "Ang volume shopper ay miyembrong minarkahang malakas bumili. I-update ang tag sa ibaba.",
  "md.enableVolumeShopper": "I-enable ang Volume Shopper",
  "md.disableVolumeShopper": "I-disable ang Volume Shopper",
  "md.volumeShopperUpdated": "Na-update ang volume shopper.",
  "md.updateFailed": "Hindi ma-save ang pagbabago.",
  "md.printNeedsPhoto": "Hindi maaaring mag-print hangga't walang larawan",
  "md.printNotActive": "Hindi maaaring mag-print: ang account ay {status}",
  "md.printTemporary": "Hindi maaaring mag-print: 10 minuto lang ang renew ng card",
  "md.supplementaryAccount": "Supplementary na Account",
  "md.addSupplementary": "Magdagdag ng Supplementary",
  "md.hasSupplementary": "May supplementary card na nakakabit sa account na ito.",
  "md.noSupplementary": "Wala pang supplementary card.",
  "md.noPermission": "Wala kang pahintulot",
  "hist.notes": "Mga Tala",
  "hist.note": "Tala",
  "hist.club": "Club",
  "hist.leftAt": "Iniwan Sa",
  "hist.promoCode": "Promo Code",
  "hist.complaintReason": "Dahilan ng Reklamo",
  "hist.requestedBy": "Hiniling Ni",
  "hist.endorsedBy": "Inendorso Ni",
  "hist.approvedBy": "Inaprubahan Ni",
  "new.taglineFor": "Magdagdag ng bagong {tier} sa pamilyang SNR",
  "new.removePicture": "Tanggalin ang larawan",
  "new.captureGuide": "Gabay sa pagkuha",
  "new.guide1": "Siguraduhing ikaw ang taong nag-a-apply.",
  "new.guide2": "Siguraduhing nasa maliwanag na lugar ka.",
  "new.guide3": "Ilagay ang iyong mukha sa loob ng frame.",
  "new.guide4": "Siguraduhing direktang nakikita ang mukha, walang anggulo sa gilid.",
};

const DICTIONARIES: Record<Locale, Dictionary> = { en, fil };

export function translate(locale: Locale, key: TranslationKey): string {
  return DICTIONARIES[locale][key];
}
